import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import mammoth from 'mammoth';

const ExcelPreview: React.FC<{ blob: Blob; fileName: string; downloadUrl: string }> = ({ blob, fileName, downloadUrl }) => {
  const [sheets, setSheets] = useState<{ name: string; rows: any[][] }[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    blob.arrayBuffer().then((buf) => {
      try {
        const wb = XLSX.read(buf, { type: 'array' });
        const parsed = wb.SheetNames.map((name) => ({
          name,
          rows: XLSX.utils.sheet_to_json<any[]>(wb.Sheets[name], { header: 1, defval: '' }),
        }));
        if (!cancelled) { setSheets(parsed); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError('Failed to parse Excel file.'); setLoading(false); }
      }
    });
    return () => { cancelled = true; };
  }, [blob]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent"></div>
      <span className="text-xs font-medium">Parsing spreadsheet...</span>
    </div>
  );

  if (error || sheets.length === 0) return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-20 h-20 rounded-2xl bg-emerald-600 flex items-center justify-center mb-4 shadow-lg">
        <span className="material-symbols-outlined text-[40px] text-white">table_chart</span>
      </div>
      <h5 className="text-base font-bold text-slate-800 mb-1">Excel Spreadsheet</h5>
      <p className="text-xs text-slate-500 max-w-xs leading-relaxed mb-6">{error || 'No data found in this file.'}</p>
      <a href={downloadUrl} download={fileName} className="px-6 py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-md flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px]">download</span>
        Download to Open
      </a>
    </div>
  );

  const current = sheets[activeSheet];
  const headers: any[] = current.rows[0] || [];
  const dataRows = current.rows.slice(1);

  return (
    <div className="w-full h-full flex flex-col">
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <div className="flex gap-1 px-2 pt-2 border-b border-slate-200 bg-white shrink-0 overflow-x-auto">
          {sheets.map((s, i) => (
            <button
              key={s.name}
              onClick={() => setActiveSheet(i)}
              className={`px-4 py-1.5 text-xs font-semibold rounded-t-lg border border-b-0 transition-colors whitespace-nowrap ${i === activeSheet ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      {/* Table */}
      <div className="overflow-auto flex-1 rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="text-xs text-left border-collapse min-w-max">
          {headers.length > 0 && (
            <thead className="sticky top-0 bg-emerald-50 z-10">
              <tr>
                {headers.map((h, ci) => (
                  <th key={ci} className="px-3 py-2 font-semibold text-emerald-800 border border-slate-200 whitespace-nowrap max-w-[200px] truncate">
                    {String(h)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                {headers.map((_h, ci) => (
                  <td key={ci} className="px-3 py-1.5 border border-slate-100 text-slate-700 max-w-[200px] truncate">
                    {String((row as any[])[ci] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {dataRows.length === 0 && (
          <div className="py-8 text-center text-xs text-slate-400">This sheet is empty.</div>
        )}
      </div>
      <p className="text-[10px] text-slate-400 mt-2 text-center">{dataRows.length} rows · {headers.length} columns · Download for full formatting</p>
    </div>
  );
};

const WordPreview: React.FC<{ blob: Blob; fileName: string; downloadUrl: string }> = ({ blob, fileName, downloadUrl }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    blob.arrayBuffer().then((buf) => {
      return mammoth.convertToHtml({ arrayBuffer: buf });
    }).then((result) => {
      if (!cancelled) { setHtml(result.value); setLoading(false); }
    }).catch(() => {
      if (!cancelled) { setError('Failed to parse Word document.'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [blob]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      <span className="text-xs font-medium">Converting document...</span>
    </div>
  );

  if (error || !html) return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center mb-4 shadow-lg">
        <span className="material-symbols-outlined text-[40px] text-white">article</span>
      </div>
      <h5 className="text-base font-bold text-slate-800 mb-1">Word Document</h5>
      <p className="text-xs text-slate-500 max-w-xs leading-relaxed mb-6">{error || 'Could not render this document.'}</p>
      <a href={downloadUrl} download={fileName} className="px-6 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
        <span className="material-symbols-outlined text-[18px]">download</span>
        Download to Open
      </a>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="overflow-auto flex-1 rounded-lg border border-slate-200 bg-white shadow-sm p-8">
        <div
          className="prose prose-sm max-w-none text-slate-800 leading-relaxed"
          style={{ fontFamily: 'Georgia, serif' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
      <p className="text-[10px] text-slate-400 mt-2 text-center">Rendered from .docx — some formatting may differ. Download for exact layout.</p>
    </div>
  );
};

const MasterData: React.FC = () => {
  const [activeTab, setActiveTab] = useState('General');
  const [isLoading, setIsLoading] = useState(false);
  const [facilities, setFacilities] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [pointsChecklists, setPointsChecklists] = useState<any[]>([]);
  const [finalVerificationChecklists, setFinalVerificationChecklists] = useState<any[]>([]);
  const [stuffingMandatoryImages, setStuffingMandatoryImages] = useState<any[]>([]);
  const [stuffingOptionalImages, setStuffingOptionalImages] = useState<any[]>([]);

  // Fabric Inspection states
  const [fabricProductCategories, setFabricProductCategories] = useState<any[]>([]);
  const [fabricChecklists, setFabricChecklists] = useState<any[]>([]);
  const [fabricMandatoryImages, setFabricMandatoryImages] = useState<any[]>([]);
  const [fabricOptionalImages, setFabricOptionalImages] = useState<any[]>([]);
  const [activeFabricTab, setActiveFabricTab] = useState('Product Category');

  // Final Inspection states
  const [activeFinalTab, setActiveFinalTab] = useState('Product Category');
  const [finalView, setFinalView] = useState<'list' | 'defect-details'>('list');
  const [selectedFinalDefectCategory, setSelectedFinalDefectCategory] = useState<string | null>(null);
  const [sopDocuments, setSopDocuments] = useState<any[]>([]);
  const [sopPreviewDoc, setSopPreviewDoc] = useState<{name: string, url: string, mime: string, blob?: Blob} | null>(null);
  const [buyers, setBuyers] = useState<any[]>([]);
  const [selectedBuyerFolder, setSelectedBuyerFolder] = useState<any | null>(null);
  const [newBuyerName, setNewBuyerName] = useState('');
  const [isZippingFiles, setIsZippingFiles] = useState(false);
  const [fabricDefectCategories, setFabricDefectCategories] = useState<any[]>([]);
  const [fabricDefectTypes, setFabricDefectTypes] = useState<any[]>([]);
  const [fabricView, setFabricView] = useState<'list' | 'defect-details'>('list');
  const [selectedDefectCategory, setSelectedDefectCategory] = useState<string | null>(null);
  const [selectedDefectCategoryId, setSelectedDefectCategoryId] = useState<string | null>(null);

  // Line Inspection states
  const [activeLineTab, setActiveLineTab] = useState('Production Process');
  const [lineView, setLineView] = useState<'list' | 'defect-details'>('list');
  const [selectedLineDefectCategory, setSelectedLineDefectCategory] = useState<string | null>(null);
  const [selectedProductionProcess, setSelectedProductionProcess] = useState<any | null>(null);
  const [selectedLineProductCategory, setSelectedLineProductCategory] = useState<any | null>(null);
  const [lineMandatoryImages, setLineMandatoryImages] = useState<any[]>([]);
  const [lineOptionalImages, setLineOptionalImages] = useState<any[]>([]);
  const [lineChecklists, setLineChecklists] = useState<any[]>([]);
  const [productionProcesses, setProductionProcesses] = useState<any[]>([]);
  const [lineProductCategories, setLineProductCategories] = useState<any[]>([]);
  const [lineDefectCategories, setLineDefectCategories] = useState<any[]>([]);
  const [selectedLineDefectCategoryId, setSelectedLineDefectCategoryId] = useState<string | null>(null);
  const [lineVerifications, setLineVerifications] = useState<any[]>([]);
  const [lineDefectTypes, setLineDefectTypes] = useState<any[]>([]);
undefined

  // Container Inspection states
  const [activeContainerTab, setActiveContainerTab] = useState('Points Checklist');

  // Modal Visibility States
  const [showFacilityModal, setShowFacilityModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddDefectCategoryModal, setShowAddDefectCategoryModal] = useState(false);
  const [showAddDefectTypeModal, setShowAddDefectTypeModal] = useState(false);
  const [showAddChecklistModal, setShowAddChecklistModal] = useState(false);
  const [showAddFabricImageModal, setShowAddFabricImageModal] = useState<'mandatory' | 'optional' | null>(null);

  // Line Inspection Modals Visibility
  const [showAddLineProductModal, setShowAddLineProductModal] = useState(false);
  const [showAddProductionProcessModal, setShowAddProductionProcessModal] = useState(false);
  const [showAddLineChecklistModal, setShowAddLineChecklistModal] = useState(false);
  const [showAddLineImageModal, setShowAddLineImageModal] = useState<'mandatory' | 'optional' | null>(null);
  const [showAddLineVerificationModal, setShowAddLineVerificationModal] = useState(false);

  // Final Inspection Modals Visibility
  const [showAddFinalProductModal, setShowAddFinalProductModal] = useState(false);
  const [showAddFinalDefectCategoryModal, setShowAddFinalDefectCategoryModal] = useState(false);
  const [showAddFinalDefectTypeModal, setShowAddFinalDefectTypeModal] = useState(false);
  const [showAddFinalChecklistModal, setShowAddFinalChecklistModal] = useState(false);
  const [showAddFinalImageModal, setShowAddFinalImageModal] = useState<'mandatory' | 'optional' | null>(null);
  const [showAddSOPModal, setShowAddSOPModal] = useState(false);
  const [showAddBuyerModal, setShowAddBuyerModal] = useState(false);

  // Container Inspection Modals Visibility
  const [showAddContainerChecklistItemModal, setShowAddContainerChecklistItemModal] = useState(false);
  const [showAddContainerImageLabelModal, setShowAddContainerImageLabelModal] = useState<'mandatory' | 'optional' | null>(null);

  // Deletion Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteTargetType, setDeleteTargetType] = useState<string>('');

  // Editing Item States
  const [editingItem, setEditingItem] = useState<any>(null);

  // Product Category Material Rows State (for the "real" part of the request)
  const [materialRows, setMaterialRows] = useState<{ name: string; percent: string | number }[]>([{ name: 'Cotton', percent: 98 }, { name: 'Elastane', percent: 2 }]);

  // Import / Export refs
  const facilityImportInputRef = useRef<HTMLInputElement>(null);
  const supplierImportInputRef = useRef<HTMLInputElement>(null);
  const fabricDefectImportInputRef = useRef<HTMLInputElement>(null);
  const finalDefectImportInputRef = useRef<HTMLInputElement>(null);
  const lineDefectImportInputRef = useRef<HTMLInputElement>(null);
  const lineProductCatImportInputRef = useRef<HTMLInputElement>(null);
  const lineChecklistImportInputRef = useRef<HTMLInputElement>(null);
  const lineVerificationImportInputRef = useRef<HTMLInputElement>(null);
  const lineMandatoryImageImportInputRef = useRef<HTMLInputElement>(null);
  const lineOptionalImageImportInputRef = useRef<HTMLInputElement>(null);
  const productionProcessImportInputRef = useRef<HTMLInputElement>(null);
  // Fabric Inspection
  const fabricChecklistImportInputRef = useRef<HTMLInputElement>(null);
  const fabricMandatoryImageImportInputRef = useRef<HTMLInputElement>(null);
  const fabricOptionalImageImportInputRef = useRef<HTMLInputElement>(null);
  // Final Inspection
  const finalProductCatImportInputRef = useRef<HTMLInputElement>(null);
  const finalMandatoryImageImportInputRef = useRef<HTMLInputElement>(null);
  const finalOptionalImageImportInputRef = useRef<HTMLInputElement>(null);
  const finalPoChecklistImportInputRef = useRef<HTMLInputElement>(null);
  // Container Inspection
  const pointsChecklistImportInputRef = useRef<HTMLInputElement>(null);
  const finalVerificationChecklistImportInputRef = useRef<HTMLInputElement>(null);
  const stuffingMandatoryImageImportInputRef = useRef<HTMLInputElement>(null);
  const stuffingOptionalImageImportInputRef = useRef<HTMLInputElement>(null);
  type ImportEntity =
    | 'Facility' | 'Supplier'
    | 'FabricDefects' | 'FinalDefects'
    | 'ProductionProcesses'
    | 'LineDefects' | 'LineProductCategories' | 'LineChecklists' | 'LineVerifications'
    | 'LineMandatoryImages' | 'LineOptionalImages'
    | 'FabricChecklists' | 'FabricMandatoryImages' | 'FabricOptionalImages'
    | 'FinalProductCategories' | 'FinalMandatoryImages' | 'FinalOptionalImages' | 'FinalPoChecklists'
    | 'PointsChecklists' | 'FinalVerificationChecklists' | 'StuffingMandatoryImages' | 'StuffingOptionalImages';
  // Pending process-scoped import — holds the parsed file while a cross-process
  // confirmation modal is shown. Generic across all Line entities.
  const [pendingProcessImport, setPendingProcessImport] = useState<null | {
    entity: 'LineDefects' | 'LineProductCategories' | 'LineChecklists' | 'LineVerifications'
          | 'LineMandatoryImages' | 'LineOptionalImages';
    rows: any[];
    fileProcessName: string;
    currentProcessName: string;
  }>(null);
  const [isImporting, setIsImporting] = useState<null | ImportEntity>(null);
  const [importResult, setImportResult] = useState<null | {
    entity: ImportEntity;
    total: number;
    inserted: number;        // for flat entities; for FabricDefects this holds types.inserted
    replaced: number;
    failed: number;
    errors: { row: number; message: string }[];
    categoryStats?: { inserted: number; replaced: number }; // only set for FabricDefects
  }>(null);

  const tabs = [
    'General',
    'Fabric Inspection',
    'Line Inspection',
    'Final Inspection',
    'Container Inspection',
  ];

  const finalTabs = [
    'Product Category',
    'Defect Category & Type',
    'Product Images',
    'PO Checklist',
    'SOP Documents'
  ];

  // Final Inspection Product Categories (API-backed)
  const [finalProductCategories, setFinalProductCategories] = useState<any[]>([]);

  const [finalDefectCategories, setFinalDefectCategories] = useState<any[]>([]);
  const [finalDefectTypes, setFinalDefectTypes] = useState<any[]>([]);
  const [selectedFinalCategoryId, setSelectedFinalCategoryId] = useState<string | null>(null);
  const [selectedFinalCategoryName, setSelectedFinalCategoryName] = useState<string | null>(null);

  const [finalChecklists, setFinalChecklists] = useState<any[]>([]);

  const [finalMandatoryImages, setFinalMandatoryImages] = useState<any[]>([]);
  const [finalOptionalImages, setFinalOptionalImages] = useState<any[]>([]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleSaveSopDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);

      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        fetchSopDocuments();
        handleCloseModal(setShowAddSOPModal);
      } else {
        alert(data.message || 'Error saving document');
      }
    } catch (error) {
      console.error('Error saving SOP document:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBuyerFolder = async () => {
    if (!newBuyerName.trim()) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/buyers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name: newBuyerName.trim() })
      });
      const data = await response.json();
      if (data.success) {
        fetchSopBuyers();
        setNewBuyerName('');
        setShowAddBuyerModal(false);
      } else {
        alert(data.message || 'Error creating buyer folder');
      }
    } catch (error) {
      console.error('Error creating buyer folder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fabricTabs = [
    'Product Category',
    'Defect Category & Type',
    'Checklist',
    'Inspection Images'
  ];

  const lineTabs = [
    'Production Process',
    'Product Category',
    'Defect Category & Type',
    'Checklist',
    'Verification',
    'Product Images',
  ];

  const containerTabs = [
    'Points Checklist',
    'Final Verification Checklist',
    'Stuffing Images'
  ];

  useEffect(() => {
    if (activeTab === 'General') {
      fetchFacilities();
      fetchSuppliers();
    } else if (activeTab === 'Fabric Inspection') {
      fetchFabricProductCategories();
      fetchFabricDefectCategories();
      fetchFabricChecklists();
      fetchFabricImages();
    } else if (activeTab === 'Line Inspection') {
      fetchProductionProcesses();
      fetchLineProductCategories();
    } else if (activeTab === 'Final Inspection') {
      fetchFinalProductCategories();
      fetchFinalInspectionImages();
      fetchFinalPoChecklists();
      fetchFinalDefectCategories();
      fetchSopBuyers();
      fetchSopDocuments();
    } else if (activeTab === 'Container Inspection') {
      fetchPointsChecklists();
      fetchFinalVerificationChecklists();
      fetchStuffingImages();
    }
  }, [activeTab]);

  useEffect(() => {
    fetchLineDefectCategories();
    fetchLineVerifications();
    fetchLineChecklists();
    fetchLineImages();
  }, [selectedProductionProcess]);

  const fetchFinalDefectCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFinalDefectCategories(data.data);
    } catch (error) {
      console.error('Error fetching final defect categories:', error);
    }
  };

  const fetchFinalDefectTypes = async (categoryId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories/${categoryId}/types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFinalDefectTypes(data.data);
    } catch (error) {
      console.error('Error fetching final defect types:', error);
    }
  };

  const fetchFinalPoChecklists = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-po-checklists`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFinalChecklists(data.data);
    } catch (error) {
      console.error('Error fetching final PO checklists:', error);
    }
  };

  const fetchSopBuyers = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/buyers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setBuyers(data.data);
    } catch (error) {
      console.error('Error fetching SOP buyers:', error);
    }
  };

  const fetchSopDocuments = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setSopDocuments(data.data);
    } catch (error) {
      console.error('Error fetching SOP documents:', error);
    }
  };

  const handleZipAllSOPFiles = async () => {
    if (isZippingFiles) return;
    setIsZippingFiles(true);
    try {
      const zip = new JSZip();
      const token = localStorage.getItem('token');

      const buyersToZip = buyers.length > 0 ? buyers : [];
      const docsToZip = sopDocuments.length > 0 ? sopDocuments : [];

      if (docsToZip.length === 0) {
        alert('No SOP documents to zip.');
        return;
      }

      for (const buyer of buyersToZip) {
        const buyerDocs = docsToZip.filter((doc: any) => doc.buyer_id === buyer.id);
        if (buyerDocs.length === 0) continue;

        const folder = zip.folder(buyer.name);
        if (!folder) continue;

        for (const doc of buyerDocs) {
          try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents/${doc.id}/download`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) continue;
            const blob = await res.blob();
            folder.file(doc.file_name || `document_${doc.id}`, blob);
          } catch (err) {
            console.error(`Failed to download ${doc.file_name}:`, err);
          }
        }
      }

      // Handle docs with no buyer (orphaned)
      const orphanDocs = docsToZip.filter((doc: any) => !doc.buyer_id || !buyersToZip.find((b: any) => b.id === doc.buyer_id));
      if (orphanDocs.length > 0) {
        const orphanFolder = zip.folder('Other');
        for (const doc of orphanDocs) {
          try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents/${doc.id}/download`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) continue;
            const blob = await res.blob();
            orphanFolder?.file(doc.file_name || `document_${doc.id}`, blob);
          } catch (err) {
            console.error(`Failed to download ${doc.file_name}:`, err);
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SOP_Documents.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to create zip:', err);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setIsZippingFiles(false);
    }
  };

  const fetchFinalInspectionImages = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-inspection-images`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setFinalMandatoryImages(data.data.filter((img: any) => img.category === 'mandatory'));
        setFinalOptionalImages(data.data.filter((img: any) => img.category === 'optional'));
      }
    } catch (error) {
      console.error('Error fetching final inspection images:', error);
    }
  };

  const fetchFinalProductCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-product-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFinalProductCategories(data.data);
    } catch (error) {
      console.error('Error fetching final product categories:', error);
    }
  };

  const fetchFabricProductCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-product-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFabricProductCategories(data.data);
    } catch (error) {
      console.error('Error fetching fabric product categories:', error);
    }
  };

  const fetchFabricDefectCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFabricDefectCategories(data.data);
    } catch (error) {
      console.error('Error fetching fabric defect categories:', error);
    }
  };

  const fetchFabricDefectTypes = async (categoryId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories/${categoryId}/types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFabricDefectTypes(data.data);
    } catch (error) {
      console.error('Error fetching fabric defect types:', error);
    }
  };

  const fetchProductionProcesses = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/production-processes`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setProductionProcesses(data.data);
        setSelectedProductionProcess((prev: any) => {
          if (prev && data.data.some((p: any) => p.id === prev.id)) return prev;
          return data.data.length > 0 ? data.data[0] : null;
        });
      }
    } catch (error) {
      console.error('Error fetching production processes:', error);
    }
  };

  const fetchLineProductCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-product-categories`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setLineProductCategories(data.data);
    } catch (error) {
      console.error('Error fetching line product categories:', error);
    }
  };

  const fetchLineImages = async () => {
    if (!selectedProductionProcess) { setLineMandatoryImages([]); setLineOptionalImages([]); return; }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-images?process_id=${selectedProductionProcess.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setLineMandatoryImages(data.data.filter((img: any) => img.category === 'mandatory'));
        setLineOptionalImages(data.data.filter((img: any) => img.category === 'optional'));
      }
    } catch (error) {
      console.error('Error fetching line images:', error);
    }
  };

  const fetchLineDefectCategories = async () => {
    if (!selectedProductionProcess) { setLineDefectCategories([]); return; }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories?process_id=${selectedProductionProcess.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setLineDefectCategories(data.data);
    } catch (error) {
      console.error('Error fetching line defect categories:', error);
    }
  };

  const fetchLineDefectTypes = async (categoryId: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories/${categoryId}/types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setLineDefectTypes(data.data);
    } catch (error) {
      console.error('Error fetching line defect types:', error);
    }
  };

  const fetchLineVerifications = async () => {
    if (!selectedProductionProcess) { setLineVerifications([]); return; }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-verifications?process_id=${selectedProductionProcess.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setLineVerifications(data.data);
    } catch (error) {
      console.error('Error fetching line verifications:', error);
    }
  };

  const fetchFabricImages = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-images`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setFabricMandatoryImages(data.data.filter((img: any) => img.category === 'mandatory'));
        setFabricOptionalImages(data.data.filter((img: any) => img.category === 'optional'));
      }
    } catch (error) {
      console.error('Error fetching fabric images:', error);
    }
  };

  const fetchFabricChecklists = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-checklists`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setFabricChecklists(data.data);
    } catch (error) {
      console.error('Error fetching fabric checklists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLineChecklists = async () => {
    if (!selectedProductionProcess) { setLineChecklists([]); return; }
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-checklists?process_id=${selectedProductionProcess.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) setLineChecklists(data.data);
    } catch (error) {
      console.error('Error fetching line checklists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPointsChecklists = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/points-checklists`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPointsChecklists(data.data);
      }
    } catch (error) {
      console.error('Error fetching points checklists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFinalVerificationChecklists = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-verification-checklists`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setFinalVerificationChecklists(data.data);
      }
    } catch (error) {
      console.error('Error fetching final verification checklists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStuffingImages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/stuffing-images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setStuffingMandatoryImages(data.data.filter((img: any) => img.category === 'mandatory'));
        setStuffingOptionalImages(data.data.filter((img: any) => img.category === 'optional'));
      }
    } catch (error) {
      console.error('Error fetching stuffing images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFacilities = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/facilities`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setFacilities(data.data);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/suppliers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSuppliers(data.data);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Import / Export helpers (Facilities & Suppliers) ────────────────────
  const downloadXlsx = (sheetName: string, filename: string, rows: any[]) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const handleExportFacilities = () => {
    const rows = facilities.map((f) => ({
      Name: f.name || '',
      Code: f.code || '',
      Address: f.address || '',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadXlsx('Facilities', `facilities-${stamp}.xlsx`, rows.length ? rows : [{ Name: '', Code: '', Address: '' }]);
  };

  const handleExportSuppliers = () => {
    const rows = suppliers.map((s) => ({
      Name: s.name || '',
      GST: s.gst || '',
      Location: s.location || '',
      'Contact Person': s.contact || '',
      Phone: s.phone || '',
      Email: s.email || '',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadXlsx(
      'Suppliers',
      `suppliers-${stamp}.xlsx`,
      rows.length ? rows : [{ Name: '', GST: '', Location: '', 'Contact Person': '', Phone: '', Email: '' }]
    );
  };

  // Normalize header keys: "Facility Name" / "Name" / "name" → "name"
  const headerKey = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');

  const parseXlsxFile = async (file: File): Promise<Record<string, any>[]> => {
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (!ws) return [];
    const raw = XLSX.utils.sheet_to_json<Record<string, any>>(ws, { defval: '' });
    return raw;
  };

  const handleImportFacilitiesFile = async (file: File) => {
    setIsImporting('Facility');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          name: m['name'] ?? m['facilityname'] ?? '',
          code: m['code'] ?? m['facilitycode'] ?? '',
          address: m['address'] ?? m['location'] ?? '',
        };
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/facilities/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({ entity: 'Facility', ...data.data });
      fetchFacilities();
    } catch (err: any) {
      alert(err.message || 'Failed to import facilities');
    } finally {
      setIsImporting(null);
      if (facilityImportInputRef.current) facilityImportInputRef.current.value = '';
    }
  };

  const handleImportSuppliersFile = async (file: File) => {
    setIsImporting('Supplier');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          name: m['name'] ?? m['suppliername'] ?? '',
          gst: m['gst'] ?? m['gstnumber'] ?? '',
          location: m['location'] ?? '',
          contact: m['contact'] ?? m['contactperson'] ?? '',
          phone: m['phone'] ?? m['phonenumber'] ?? m['mobile'] ?? '',
          email: m['email'] ?? m['emailid'] ?? '',
        };
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/suppliers/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({ entity: 'Supplier', ...data.data });
      fetchSuppliers();
    } catch (err: any) {
      alert(err.message || 'Failed to import suppliers');
    } finally {
      setIsImporting(null);
      if (supplierImportInputRef.current) supplierImportInputRef.current.value = '';
    }
  };

  // ── Fabric Defect Categories & Types — Import / Export ──────────────────
  // Single flat sheet, one row per defect type. Empty category rows are
  // allowed (just create/reuse the category).
  const handleExportFabricDefects = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      // Fetch each category's types in parallel
      const typeResponses = await Promise.all(
        fabricDefectCategories.map(async (dc: any) => {
          const r = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories/${dc.id}/types`, { headers });
          const d = await r.json();
          return { category: dc.name, types: d.success ? d.data : [] };
        })
      );

      const rows: { 'Defect Category': string; 'DCL Code': string; 'Defect Type': string }[] = [];
      for (const { category, types } of typeResponses) {
        if (types.length === 0) {
          rows.push({ 'Defect Category': category, 'DCL Code': '', 'Defect Type': '' });
        } else {
          for (const t of types) {
            rows.push({ 'Defect Category': category, 'DCL Code': t.code || '', 'Defect Type': t.name || '' });
          }
        }
      }
      if (rows.length === 0) {
        rows.push({ 'Defect Category': '', 'DCL Code': '', 'Defect Type': '' });
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadXlsx('Fabric Defects', `fabric-defects-${stamp}.xlsx`, rows);
    } catch (err: any) {
      alert(err.message || 'Failed to export fabric defects');
    }
  };

  const handleImportFabricDefectsFile = async (file: File) => {
    setIsImporting('FabricDefects');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          category: m['defectcategory'] ?? m['category'] ?? '',
          code: m['dclcode'] ?? m['code'] ?? '',
          name: m['defecttype'] ?? m['name'] ?? m['type'] ?? '',
        };
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({
        entity: 'FabricDefects',
        total: data.data.total,
        inserted: data.data.types.inserted,
        replaced: data.data.types.replaced,
        failed: data.data.types.failed,
        errors: data.data.types.errors || [],
        categoryStats: data.data.categories,
      });
      fetchFabricDefectCategories();
      if (selectedDefectCategoryId) fetchFabricDefectTypes(selectedDefectCategoryId);
    } catch (err: any) {
      alert(err.message || 'Failed to import fabric defects');
    } finally {
      setIsImporting(null);
      if (fabricDefectImportInputRef.current) fabricDefectImportInputRef.current.value = '';
    }
  };

  // ── Final Defect Categories & Types — Import / Export ───────────────────
  // Same hierarchical xlsx shape as Fabric Defects: one row per defect type,
  // empty-category rows allowed.
  const handleExportFinalDefects = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const typeResponses = await Promise.all(
        finalDefectCategories.map(async (dc: any) => {
          const r = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories/${dc.id}/types`, { headers });
          const d = await r.json();
          return { category: dc.name, types: d.success ? d.data : [] };
        })
      );

      const rows: { 'Defect Category': string; 'DCL Code': string; 'Defect Type': string }[] = [];
      for (const { category, types } of typeResponses) {
        if (types.length === 0) {
          rows.push({ 'Defect Category': category, 'DCL Code': '', 'Defect Type': '' });
        } else {
          for (const t of types) {
            rows.push({ 'Defect Category': category, 'DCL Code': t.code || '', 'Defect Type': t.name || '' });
          }
        }
      }
      if (rows.length === 0) {
        rows.push({ 'Defect Category': '', 'DCL Code': '', 'Defect Type': '' });
      }
      const stamp = new Date().toISOString().slice(0, 10);
      downloadXlsx('Final Defects', `final-defects-${stamp}.xlsx`, rows);
    } catch (err: any) {
      alert(err.message || 'Failed to export final defects');
    }
  };

  const handleImportFinalDefectsFile = async (file: File) => {
    setIsImporting('FinalDefects');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          category: m['defectcategory'] ?? m['category'] ?? '',
          code: m['dclcode'] ?? m['code'] ?? '',
          name: m['defecttype'] ?? m['name'] ?? m['type'] ?? '',
        };
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({
        entity: 'FinalDefects',
        total: data.data.total,
        inserted: data.data.types.inserted,
        replaced: data.data.types.replaced,
        failed: data.data.types.failed,
        errors: data.data.types.errors || [],
        categoryStats: data.data.categories,
      });
      fetchFinalDefectCategories();
      if (selectedFinalCategoryId) fetchFinalDefectTypes(selectedFinalCategoryId);
    } catch (err: any) {
      alert(err.message || 'Failed to import final defects');
    } finally {
      setIsImporting(null);
      if (finalDefectImportInputRef.current) finalDefectImportInputRef.current.value = '';
    }
  };

  // ── Line Defect Categories & Types — Import / Export (process-scoped) ───
  // Categories belong to a specific Production Process. Sheet name carries
  // the source process name so we can warn on mismatched imports.
  const sanitizeSheetName = (s: string) => {
    // Excel sheet names: max 31 chars, no [ ] / \ ? * :
    return (s || 'Sheet1').replace(/[\[\]/\\?*:]/g, '_').slice(0, 31).trim() || 'Sheet1';
  };
  const slugify = (s: string) =>
    (s || 'process').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'process';

  const handleExportLineDefects = async () => {
    if (!selectedProductionProcess) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const typeResponses = await Promise.all(
        lineDefectCategories.map(async (dc: any) => {
          const r = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories/${dc.id}/types`, { headers });
          const d = await r.json();
          return { category: dc.name, types: d.success ? d.data : [] };
        })
      );

      const rows: { 'Defect Category': string; 'DCL Code': string; 'Defect Type': string }[] = [];
      for (const { category, types } of typeResponses) {
        if (types.length === 0) {
          rows.push({ 'Defect Category': category, 'DCL Code': '', 'Defect Type': '' });
        } else {
          for (const t of types) {
            rows.push({ 'Defect Category': category, 'DCL Code': t.code || '', 'Defect Type': t.name || '' });
          }
        }
      }
      if (rows.length === 0) {
        rows.push({ 'Defect Category': '', 'DCL Code': '', 'Defect Type': '' });
      }
      const processName = selectedProductionProcess.name || 'Process';
      const stamp = new Date().toISOString().slice(0, 10);
      // Sheet name = process name (used as the "from which process" marker on re-import)
      downloadXlsx(sanitizeSheetName(processName), `line-defects-${slugify(processName)}-${stamp}.xlsx`, rows);
    } catch (err: any) {
      alert(err.message || 'Failed to export line defects');
    }
  };

  // Read the first sheet name from an xlsx file (used to detect cross-process imports)
  const readXlsxSheetName = async (file: File): Promise<string | null> => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      return wb.SheetNames[0] || null;
    } catch {
      return null;
    }
  };

  const submitLineImport = async (rows: { category: string; code: string; name: string }[]) => {
    if (!selectedProductionProcess) return;
    setIsImporting('LineDefects');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ process_id: selectedProductionProcess.id, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({
        entity: 'LineDefects',
        total: data.data.total,
        inserted: data.data.types.inserted,
        replaced: data.data.types.replaced,
        failed: data.data.types.failed,
        errors: data.data.types.errors || [],
        categoryStats: data.data.categories,
      });
      fetchLineDefectCategories();
      if (selectedLineDefectCategoryId) fetchLineDefectTypes(selectedLineDefectCategoryId);
    } catch (err: any) {
      alert(err.message || 'Failed to import line defects');
    } finally {
      setIsImporting(null);
      if (lineDefectImportInputRef.current) lineDefectImportInputRef.current.value = '';
    }
  };

  const handleImportLineDefectsFile = async (file: File) => {
    if (!selectedProductionProcess) return;

    // Parse rows + read sheet name to detect a cross-process import
    let rows: { category: string; code: string; name: string }[] = [];
    let fileProcessName: string | null = null;
    try {
      const raw = await parseXlsxFile(file);
      rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          category: m['defectcategory'] ?? m['category'] ?? '',
          code: m['dclcode'] ?? m['code'] ?? '',
          name: m['defecttype'] ?? m['name'] ?? m['type'] ?? '',
        };
      });
      fileProcessName = await readXlsxSheetName(file);
    } catch (err: any) {
      alert(err.message || 'Failed to parse file');
      if (lineDefectImportInputRef.current) lineDefectImportInputRef.current.value = '';
      return;
    }

    const currentProcessName = selectedProductionProcess.name || '';
    const fileCanonical = sanitizeSheetName(fileProcessName || '').toLowerCase();
    const currentCanonical = sanitizeSheetName(currentProcessName).toLowerCase();
    // Generic sheet names like "Sheet1" or empty → no metadata, skip warning
    const fileHasProcessTag = fileCanonical && !/^sheet\d*$/i.test(fileCanonical);

    if (fileHasProcessTag && fileCanonical !== currentCanonical) {
      // Cross-process import — show confirmation modal
      setPendingProcessImport({
        entity: 'LineDefects',
        rows,
        fileProcessName: fileProcessName || '',
        currentProcessName,
      });
      return;
    }

    await submitLineImport(rows);
  };

  // ── Line Product Categories — Import / Export (process-scoped) ──────────
  const handleExportLineProductCategories = () => {
    if (!selectedProductionProcess) return;
    const rows = lineProductCategories
      .filter((pc: any) => pc.process_id === selectedProductionProcess.id)
      .map((pc: any) => ({ Name: pc.name || '' }));
    const stamp = new Date().toISOString().slice(0, 10);
    const processName = selectedProductionProcess.name || 'Process';
    downloadXlsx(
      sanitizeSheetName(processName),
      `line-product-categories-${slugify(processName)}-${stamp}.xlsx`,
      rows.length ? rows : [{ Name: '' }]
    );
  };

  const submitLineProductCategoriesImport = async (rows: { name: string }[]) => {
    if (!selectedProductionProcess) return;
    setIsImporting('LineProductCategories');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-product-categories/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ process_id: selectedProductionProcess.id, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'LineProductCategories', ...data.data });
      fetchLineProductCategories();
    } catch (err: any) {
      alert(err.message || 'Failed to import product categories');
    } finally {
      setIsImporting(null);
      if (lineProductCatImportInputRef.current) lineProductCatImportInputRef.current.value = '';
    }
  };

  const handleImportLineProductCategoriesFile = async (file: File) => {
    if (!selectedProductionProcess) return;
    let rows: { name: string }[] = [];
    let fileProcessName: string | null = null;
    try {
      const raw = await parseXlsxFile(file);
      rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['name'] ?? m['categoryname'] ?? '' };
      });
      fileProcessName = await readXlsxSheetName(file);
    } catch (err: any) {
      alert(err.message || 'Failed to parse file');
      if (lineProductCatImportInputRef.current) lineProductCatImportInputRef.current.value = '';
      return;
    }

    const currentProcessName = selectedProductionProcess.name || '';
    const fileCanonical = sanitizeSheetName(fileProcessName || '').toLowerCase();
    const currentCanonical = sanitizeSheetName(currentProcessName).toLowerCase();
    const fileHasProcessTag = fileCanonical && !/^sheet\d*$/i.test(fileCanonical);
    if (fileHasProcessTag && fileCanonical !== currentCanonical) {
      setPendingProcessImport({
        entity: 'LineProductCategories',
        rows,
        fileProcessName: fileProcessName || '',
        currentProcessName,
      });
      return;
    }
    await submitLineProductCategoriesImport(rows);
  };

  // ── Line Checklists — Import / Export (process-scoped) ──────────────────
  const handleExportLineChecklists = () => {
    if (!selectedProductionProcess) return;
    const rows = lineChecklists.map((c: any) => ({
      Title: c.title || '',
      Description: c.description || '',
      'Evidence Required': c.evidence_required ? 'Yes' : 'No',
    }));
    const stamp = new Date().toISOString().slice(0, 10);
    const processName = selectedProductionProcess.name || 'Process';
    downloadXlsx(
      sanitizeSheetName(processName),
      `line-checklists-${slugify(processName)}-${stamp}.xlsx`,
      rows.length ? rows : [{ Title: '', Description: '', 'Evidence Required': 'No' }]
    );
  };

  const submitLineChecklistsImport = async (rows: { title: string; description: string; evidence_required: any }[]) => {
    if (!selectedProductionProcess) return;
    setIsImporting('LineChecklists');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-checklists/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ process_id: selectedProductionProcess.id, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'LineChecklists', ...data.data });
      fetchLineChecklists();
    } catch (err: any) {
      alert(err.message || 'Failed to import checklists');
    } finally {
      setIsImporting(null);
      if (lineChecklistImportInputRef.current) lineChecklistImportInputRef.current.value = '';
    }
  };

  const handleImportLineChecklistsFile = async (file: File) => {
    if (!selectedProductionProcess) return;
    let rows: { title: string; description: string; evidence_required: any }[] = [];
    let fileProcessName: string | null = null;
    try {
      const raw = await parseXlsxFile(file);
      rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          title: m['title'] ?? m['checklisttitle'] ?? '',
          description: m['description'] ?? m['desc'] ?? '',
          evidence_required: m['evidencerequired'] ?? m['evidence'] ?? '',
        };
      });
      fileProcessName = await readXlsxSheetName(file);
    } catch (err: any) {
      alert(err.message || 'Failed to parse file');
      if (lineChecklistImportInputRef.current) lineChecklistImportInputRef.current.value = '';
      return;
    }

    const currentProcessName = selectedProductionProcess.name || '';
    const fileCanonical = sanitizeSheetName(fileProcessName || '').toLowerCase();
    const currentCanonical = sanitizeSheetName(currentProcessName).toLowerCase();
    const fileHasProcessTag = fileCanonical && !/^sheet\d*$/i.test(fileCanonical);
    if (fileHasProcessTag && fileCanonical !== currentCanonical) {
      setPendingProcessImport({
        entity: 'LineChecklists',
        rows,
        fileProcessName: fileProcessName || '',
        currentProcessName,
      });
      return;
    }
    await submitLineChecklistsImport(rows);
  };

  // ── Line Verification Fields — Import / Export (process-scoped) ─────────
  const handleExportLineVerifications = () => {
    if (!selectedProductionProcess) return;
    const rows = lineVerifications.map((v: any) => ({ 'Field Name': v.name || '' }));
    const stamp = new Date().toISOString().slice(0, 10);
    const processName = selectedProductionProcess.name || 'Process';
    downloadXlsx(
      sanitizeSheetName(processName),
      `line-verifications-${slugify(processName)}-${stamp}.xlsx`,
      rows.length ? rows : [{ 'Field Name': '' }]
    );
  };

  const submitLineVerificationsImport = async (rows: { name: string }[]) => {
    if (!selectedProductionProcess) return;
    setIsImporting('LineVerifications');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-verifications/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ process_id: selectedProductionProcess.id, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'LineVerifications', ...data.data });
      fetchLineVerifications();
    } catch (err: any) {
      alert(err.message || 'Failed to import verification fields');
    } finally {
      setIsImporting(null);
      if (lineVerificationImportInputRef.current) lineVerificationImportInputRef.current.value = '';
    }
  };

  const handleImportLineVerificationsFile = async (file: File) => {
    if (!selectedProductionProcess) return;
    let rows: { name: string }[] = [];
    let fileProcessName: string | null = null;
    try {
      const raw = await parseXlsxFile(file);
      rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['fieldname'] ?? m['name'] ?? '' };
      });
      fileProcessName = await readXlsxSheetName(file);
    } catch (err: any) {
      alert(err.message || 'Failed to parse file');
      if (lineVerificationImportInputRef.current) lineVerificationImportInputRef.current.value = '';
      return;
    }

    const currentProcessName = selectedProductionProcess.name || '';
    const fileCanonical = sanitizeSheetName(fileProcessName || '').toLowerCase();
    const currentCanonical = sanitizeSheetName(currentProcessName).toLowerCase();
    const fileHasProcessTag = fileCanonical && !/^sheet\d*$/i.test(fileCanonical);
    if (fileHasProcessTag && fileCanonical !== currentCanonical) {
      setPendingProcessImport({
        entity: 'LineVerifications',
        rows,
        fileProcessName: fileProcessName || '',
        currentProcessName,
      });
      return;
    }
    await submitLineVerificationsImport(rows);
  };

  // ── Line Product Images — Import / Export (process-scoped, per-category) ─
  // Two sections (Mandatory + Optional) share one backend endpoint.
  // The `category` param routes to the right list + dedup scope.
  const imageRefForCategory = (category: 'mandatory' | 'optional') =>
    category === 'mandatory' ? lineMandatoryImageImportInputRef : lineOptionalImageImportInputRef;
  const imageEntityForCategory = (category: 'mandatory' | 'optional'): 'LineMandatoryImages' | 'LineOptionalImages' =>
    category === 'mandatory' ? 'LineMandatoryImages' : 'LineOptionalImages';

  const handleExportLineImages = (category: 'mandatory' | 'optional') => {
    if (!selectedProductionProcess) return;
    const list = category === 'mandatory' ? lineMandatoryImages : lineOptionalImages;
    const rows = list.map((img: any) => ({ 'Image Name': img.label || '' }));
    const stamp = new Date().toISOString().slice(0, 10);
    const processName = selectedProductionProcess.name || 'Process';
    downloadXlsx(
      sanitizeSheetName(processName),
      `line-${category}-images-${slugify(processName)}-${stamp}.xlsx`,
      rows.length ? rows : [{ 'Image Name': '' }]
    );
  };

  const submitLineImagesImport = async (
    rows: { label: string }[],
    category: 'mandatory' | 'optional'
  ) => {
    if (!selectedProductionProcess) return;
    const entity = imageEntityForCategory(category);
    setIsImporting(entity);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/line-images/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ process_id: selectedProductionProcess.id, category, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity, ...data.data });
      fetchLineImages();
    } catch (err: any) {
      alert(err.message || 'Failed to import images');
    } finally {
      setIsImporting(null);
      const ref = imageRefForCategory(category);
      if (ref.current) ref.current.value = '';
    }
  };

  const handleImportLineImagesFile = async (file: File, category: 'mandatory' | 'optional') => {
    if (!selectedProductionProcess) return;
    let rows: { label: string }[] = [];
    let fileProcessName: string | null = null;
    try {
      const raw = await parseXlsxFile(file);
      rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { label: m['imagename'] ?? m['label'] ?? m['name'] ?? '' };
      });
      fileProcessName = await readXlsxSheetName(file);
    } catch (err: any) {
      alert(err.message || 'Failed to parse file');
      const ref = imageRefForCategory(category);
      if (ref.current) ref.current.value = '';
      return;
    }

    const currentProcessName = selectedProductionProcess.name || '';
    const fileCanonical = sanitizeSheetName(fileProcessName || '').toLowerCase();
    const currentCanonical = sanitizeSheetName(currentProcessName).toLowerCase();
    const fileHasProcessTag = fileCanonical && !/^sheet\d*$/i.test(fileCanonical);
    if (fileHasProcessTag && fileCanonical !== currentCanonical) {
      setPendingProcessImport({
        entity: imageEntityForCategory(category),
        rows,
        fileProcessName: fileProcessName || '',
        currentProcessName,
      });
      return;
    }
    await submitLineImagesImport(rows, category);
  };

  // ── Production Processes — Import / Export (global, no process scoping) ─
  // This section IS the source of production processes, so it's not scoped
  // and has no cross-process mismatch warning.
  const handleExportProductionProcesses = () => {
    const rows = productionProcesses.map((p: any) => ({ Name: p.name || '' }));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadXlsx(
      'Production Processes',
      `production-processes-${stamp}.xlsx`,
      rows.length ? rows : [{ Name: '' }]
    );
  };

  const handleImportProductionProcessesFile = async (file: File) => {
    setIsImporting('ProductionProcesses');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['name'] ?? m['processname'] ?? '' };
      });

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/production-processes/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');

      setImportResult({ entity: 'ProductionProcesses', ...data.data });
      fetchProductionProcesses();
    } catch (err: any) {
      alert(err.message || 'Failed to import production processes');
    } finally {
      setIsImporting(null);
      if (productionProcessImportInputRef.current) productionProcessImportInputRef.current.value = '';
    }
  };

  // ── Generic helpers for global flat imports (no process scope) ──────────
  const exportFlatRows = (sheetName: string, filename: string, rows: Record<string, any>[], emptyShape: Record<string, any>) => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadXlsx(sheetName, filename.replace('{stamp}', stamp), rows.length ? rows : [emptyShape]);
  };

  // ── Fabric Inspection: Checklists ───────────────────────────────────────
  const handleExportFabricChecklists = () => {
    const rows = fabricChecklists.map((c: any) => ({
      Title: c.title || '', Description: c.description || '', 'Evidence Required': c.evidence_required ? 'Yes' : 'No',
    }));
    exportFlatRows('Fabric Checklists', `fabric-checklists-{stamp}.xlsx`, rows, { Title: '', Description: '', 'Evidence Required': 'No' });
  };
  const handleImportFabricChecklistsFile = async (file: File) => {
    setIsImporting('FabricChecklists');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          title: m['title'] ?? m['checklisttitle'] ?? '',
          description: m['description'] ?? m['desc'] ?? '',
          evidence_required: m['evidencerequired'] ?? m['evidence'] ?? '',
        };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-checklists/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'FabricChecklists', ...data.data });
      fetchFabricChecklists();
    } catch (err: any) {
      alert(err.message || 'Failed to import fabric checklists');
    } finally {
      setIsImporting(null);
      if (fabricChecklistImportInputRef.current) fabricChecklistImportInputRef.current.value = '';
    }
  };

  // ── Fabric Inspection: Inspection Images (Mandatory + Optional) ─────────
  const fabricImageRefForCategory = (cat: 'mandatory' | 'optional') =>
    cat === 'mandatory' ? fabricMandatoryImageImportInputRef : fabricOptionalImageImportInputRef;
  const fabricImageEntityForCategory = (cat: 'mandatory' | 'optional'): 'FabricMandatoryImages' | 'FabricOptionalImages' =>
    cat === 'mandatory' ? 'FabricMandatoryImages' : 'FabricOptionalImages';
  const handleExportFabricImages = (category: 'mandatory' | 'optional') => {
    const list = category === 'mandatory' ? fabricMandatoryImages : fabricOptionalImages;
    const rows = list.map((img: any) => ({ 'Image Name': img.label || '' }));
    exportFlatRows(`Fabric ${category[0].toUpperCase() + category.slice(1)} Images`, `fabric-${category}-images-{stamp}.xlsx`, rows, { 'Image Name': '' });
  };
  const handleImportFabricImagesFile = async (file: File, category: 'mandatory' | 'optional') => {
    setIsImporting(fabricImageEntityForCategory(category));
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { label: m['imagename'] ?? m['label'] ?? m['name'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-images/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ category, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: fabricImageEntityForCategory(category), ...data.data });
      fetchFabricImages();
    } catch (err: any) {
      alert(err.message || 'Failed to import fabric images');
    } finally {
      setIsImporting(null);
      const ref = fabricImageRefForCategory(category);
      if (ref.current) ref.current.value = '';
    }
  };

  // ── Final Inspection: Product Categories ────────────────────────────────
  const handleExportFinalProductCategories = () => {
    const rows = finalProductCategories.map((c: any) => ({ Name: c.name || '' }));
    exportFlatRows('Final Product Categories', `final-product-categories-{stamp}.xlsx`, rows, { Name: '' });
  };
  const handleImportFinalProductCategoriesFile = async (file: File) => {
    setIsImporting('FinalProductCategories');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['name'] ?? m['categoryname'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-product-categories/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'FinalProductCategories', ...data.data });
      fetchFinalProductCategories();
    } catch (err: any) {
      alert(err.message || 'Failed to import final product categories');
    } finally {
      setIsImporting(null);
      if (finalProductCatImportInputRef.current) finalProductCatImportInputRef.current.value = '';
    }
  };

  // ── Final Inspection: Product Images (Mandatory + Optional) ─────────────
  const finalImageRefForCategory = (cat: 'mandatory' | 'optional') =>
    cat === 'mandatory' ? finalMandatoryImageImportInputRef : finalOptionalImageImportInputRef;
  const finalImageEntityForCategory = (cat: 'mandatory' | 'optional'): 'FinalMandatoryImages' | 'FinalOptionalImages' =>
    cat === 'mandatory' ? 'FinalMandatoryImages' : 'FinalOptionalImages';
  const handleExportFinalImages = (category: 'mandatory' | 'optional') => {
    const list = category === 'mandatory' ? finalMandatoryImages : finalOptionalImages;
    const rows = list.map((img: any) => ({ 'Image Name': img.label || '' }));
    exportFlatRows(`Final ${category[0].toUpperCase() + category.slice(1)} Images`, `final-${category}-images-{stamp}.xlsx`, rows, { 'Image Name': '' });
  };
  const handleImportFinalImagesFile = async (file: File, category: 'mandatory' | 'optional') => {
    setIsImporting(finalImageEntityForCategory(category));
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { label: m['imagename'] ?? m['label'] ?? m['name'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-inspection-images/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ category, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: finalImageEntityForCategory(category), ...data.data });
      fetchFinalInspectionImages();
    } catch (err: any) {
      alert(err.message || 'Failed to import final inspection images');
    } finally {
      setIsImporting(null);
      const ref = finalImageRefForCategory(category);
      if (ref.current) ref.current.value = '';
    }
  };

  // ── Final Inspection: PO Checklist ──────────────────────────────────────
  const handleExportFinalPoChecklists = () => {
    const rows = finalChecklists.map((c: any) => ({
      Title: c.title || '', Description: c.description || '', 'Evidence Required': c.evidence_required ? 'Yes' : 'No',
    }));
    exportFlatRows('Final PO Checklists', `final-po-checklists-{stamp}.xlsx`, rows, { Title: '', Description: '', 'Evidence Required': 'No' });
  };
  const handleImportFinalPoChecklistsFile = async (file: File) => {
    setIsImporting('FinalPoChecklists');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return {
          title: m['title'] ?? m['checklisttitle'] ?? '',
          description: m['description'] ?? m['desc'] ?? '',
          evidence_required: m['evidencerequired'] ?? m['evidence'] ?? '',
        };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-po-checklists/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'FinalPoChecklists', ...data.data });
      fetchFinalPoChecklists();
    } catch (err: any) {
      alert(err.message || 'Failed to import PO checklists');
    } finally {
      setIsImporting(null);
      if (finalPoChecklistImportInputRef.current) finalPoChecklistImportInputRef.current.value = '';
    }
  };

  // ── Container Inspection: Points Checklist ──────────────────────────────
  const handleExportPointsChecklists = () => {
    const rows = pointsChecklists.map((c: any) => ({ 'Checklist Item': c.name || '' }));
    exportFlatRows('Points Checklist', `points-checklists-{stamp}.xlsx`, rows, { 'Checklist Item': '' });
  };
  const handleImportPointsChecklistsFile = async (file: File) => {
    setIsImporting('PointsChecklists');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['checklistitem'] ?? m['name'] ?? m['item'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/points-checklists/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'PointsChecklists', ...data.data });
      fetchPointsChecklists();
    } catch (err: any) {
      alert(err.message || 'Failed to import points checklists');
    } finally {
      setIsImporting(null);
      if (pointsChecklistImportInputRef.current) pointsChecklistImportInputRef.current.value = '';
    }
  };

  // ── Container Inspection: Final Verification Checklist ──────────────────
  const handleExportFinalVerificationChecklists = () => {
    const rows = finalVerificationChecklists.map((c: any) => ({ 'Checklist Item': c.name || '' }));
    exportFlatRows('Final Verification Checklist', `final-verification-checklists-{stamp}.xlsx`, rows, { 'Checklist Item': '' });
  };
  const handleImportFinalVerificationChecklistsFile = async (file: File) => {
    setIsImporting('FinalVerificationChecklists');
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { name: m['checklistitem'] ?? m['name'] ?? m['item'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/final-verification-checklists/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: 'FinalVerificationChecklists', ...data.data });
      fetchFinalVerificationChecklists();
    } catch (err: any) {
      alert(err.message || 'Failed to import final verification checklists');
    } finally {
      setIsImporting(null);
      if (finalVerificationChecklistImportInputRef.current) finalVerificationChecklistImportInputRef.current.value = '';
    }
  };

  // ── Container Inspection: Stuffing Images (Mandatory + Optional) ────────
  const stuffingImageRefForCategory = (cat: 'mandatory' | 'optional') =>
    cat === 'mandatory' ? stuffingMandatoryImageImportInputRef : stuffingOptionalImageImportInputRef;
  const stuffingImageEntityForCategory = (cat: 'mandatory' | 'optional'): 'StuffingMandatoryImages' | 'StuffingOptionalImages' =>
    cat === 'mandatory' ? 'StuffingMandatoryImages' : 'StuffingOptionalImages';
  const handleExportStuffingImages = (category: 'mandatory' | 'optional') => {
    const list = category === 'mandatory' ? stuffingMandatoryImages : stuffingOptionalImages;
    const rows = list.map((i: any) => ({ 'Image Label': i.label || '' }));
    exportFlatRows(`Stuffing ${category[0].toUpperCase() + category.slice(1)} Images`, `stuffing-${category}-images-{stamp}.xlsx`, rows, { 'Image Label': '' });
  };
  const handleImportStuffingImagesFile = async (file: File, category: 'mandatory' | 'optional') => {
    setIsImporting(stuffingImageEntityForCategory(category));
    try {
      const raw = await parseXlsxFile(file);
      const rows = raw.map((r) => {
        const m: Record<string, any> = {};
        for (const [k, v] of Object.entries(r)) m[headerKey(k)] = v;
        return { label: m['imagelabel'] ?? m['label'] ?? m['name'] ?? '' };
      });
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/stuffing-images/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ category, rows }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Import failed');
      setImportResult({ entity: stuffingImageEntityForCategory(category), ...data.data });
      fetchStuffingImages();
    } catch (err: any) {
      alert(err.message || 'Failed to import stuffing images');
    } finally {
      setIsImporting(null);
      const ref = stuffingImageRefForCategory(category);
      if (ref.current) ref.current.value = '';
    }
  };

  const checklists = [
    { title: 'Edge Alignment Check', desc: 'Verify that fabric edges are perfectly aligned during the rolling process.', evidence: true },
    { title: 'Selvage Integrity', desc: 'Check for any frayed edges or loose threads along the selvage.', evidence: false },
    { title: 'Width Uniformity', desc: 'Measure width at start, middle, and end of the roll to ensure consistency.', evidence: true },
    { title: 'Packing Standards', desc: 'Ensure polythene wrap thickness and core tube diameter meet specifications.', evidence: true },
    { title: 'Lab Report Attachment', desc: 'Check if the physical lab report matches the digital roll ID.', evidence: false },
    { title: 'Face Side Identification', desc: 'Verify the "Face" sticker is placed on the correct side of the fabric.', evidence: true },
  ];








  // Container Inspection Mock Data - Final Verification


  const handleEdit = (item: any, modalSetter: (val: boolean) => void) => {
    setEditingItem(item);
    modalSetter(true);
  };

  const openDeleteModal = (item: any, targetType: string) => {
    setItemToDelete(item);
    setDeleteTargetType(targetType);
    setShowDeleteModal(true);
  };

  const handleCloseModal = (modalSetter: any) => {
    setEditingItem(null);
    if (typeof modalSetter === 'function') {
      modalSetter(false);
    } else {
      setShowAddLineImageModal(null);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setIsLoading(true);
    try {
      const endpoint = deleteTargetType === 'Facility'
        ? `/api/v1/facilities/${itemToDelete.id}`
        : deleteTargetType === 'Supplier'
          ? `/api/v1/suppliers/${itemToDelete.id}`
          : deleteTargetType === 'Points Checklist'
            ? `/api/v1/points-checklists/${itemToDelete.id}`
            : deleteTargetType === 'Verification Item'
              ? `/api/v1/final-verification-checklists/${itemToDelete.id}`
              : deleteTargetType === 'Image Label'
                ? `/api/v1/stuffing-images/${itemToDelete.id}`
                : deleteTargetType === 'Fabric Checklist'
                  ? `/api/v1/fabric-checklists/${itemToDelete.id}`
                  : deleteTargetType === 'Fabric Image'
                    ? `/api/v1/fabric-images/${itemToDelete.id}`
                    : deleteTargetType === 'Fabric Product Category'
                      ? `/api/v1/fabric-product-categories/${itemToDelete.id}`
                      : deleteTargetType === 'Defect Category'
                        ? `/api/v1/fabric-defect-categories/${itemToDelete.id}`
                        : deleteTargetType === 'Defect Type'
                          ? `/api/v1/fabric-defect-types/${itemToDelete.id}`
                          : deleteTargetType === 'Line Defect Category'
                            ? `/api/v1/line-defect-categories/${itemToDelete.id}`
                            : deleteTargetType === 'Line Defect Type'
                              ? `/api/v1/line-defect-types/${itemToDelete.id}`
                              : deleteTargetType === 'Line Image'
                                ? `/api/v1/line-images/${itemToDelete.id}`
                                : deleteTargetType === 'Line Checklist'
                                  ? `/api/v1/line-checklists/${itemToDelete.id}`
                                  : deleteTargetType === 'Production Process'
                                ? `/api/v1/production-processes/${itemToDelete.id}`
                                : deleteTargetType === 'Line Product Category'
                                  ? `/api/v1/line-product-categories/${itemToDelete.id}`
                                  : deleteTargetType === 'Final Product Category'
                                    ? `/api/v1/final-product-categories/${itemToDelete.id}`
                                    : deleteTargetType === 'Final PO Checklist'
                                      ? `/api/v1/final-po-checklists/${itemToDelete.id}`
                                      : deleteTargetType === 'Final Defect Category'
                                        ? `/api/v1/final-defect-categories/${itemToDelete.id}`
                                        : deleteTargetType === 'Final Defect Type'
                                          ? `/api/v1/final-defect-types/${itemToDelete.id}`
                                          : deleteTargetType === 'Final Inspection Image'
                                      ? `/api/v1/final-inspection-images/${itemToDelete.id}`
                                      : deleteTargetType === 'SOP Document'
                                        ? `/api/v1/sop/documents/${itemToDelete.id}`
                                        : deleteTargetType === 'Buyer Folder'
                                          ? `/api/v1/sop/buyers/${itemToDelete.id}`
                                          : deleteTargetType === 'Line Verification'
                                            ? `/api/v1/line-verifications/${itemToDelete.id}`
                                            : '';

      if (!endpoint) {
        console.warn(`Deletion not implemented for ${deleteTargetType}`);
        setShowDeleteModal(false);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        if (deleteTargetType === 'Facility') fetchFacilities();
        if (deleteTargetType === 'Supplier') fetchSuppliers();
        if (deleteTargetType === 'Points Checklist') fetchPointsChecklists();
        if (deleteTargetType === 'Verification Item') fetchFinalVerificationChecklists();
        if (deleteTargetType === 'Image Label') fetchStuffingImages();
        if (deleteTargetType === 'Fabric Checklist') fetchFabricChecklists();
        if (deleteTargetType === 'Fabric Image') fetchFabricImages();
        if (deleteTargetType === 'Line Image') fetchLineImages();
        if (deleteTargetType === 'Line Checklist') fetchLineChecklists();
        if (deleteTargetType === 'Line Verification') fetchLineVerifications();
        if (deleteTargetType === 'Line Defect Category') fetchLineDefectCategories();
        if (deleteTargetType === 'Line Defect Type') {
          if (selectedLineDefectCategoryId) fetchLineDefectTypes(selectedLineDefectCategoryId);
          fetchLineDefectCategories();
        }
        if (deleteTargetType === 'Production Process') fetchProductionProcesses();
        if (deleteTargetType === 'Line Product Category') fetchLineProductCategories();
        if (deleteTargetType === 'Final Product Category') fetchFinalProductCategories();
        if (deleteTargetType === 'Final PO Checklist') fetchFinalPoChecklists();
        if (deleteTargetType === 'Final Defect Category') fetchFinalDefectCategories();
        if (deleteTargetType === 'Final Defect Type' && selectedFinalCategoryId) fetchFinalDefectTypes(selectedFinalCategoryId);
        if (deleteTargetType === 'Final Inspection Image') fetchFinalInspectionImages();
        if (deleteTargetType === 'SOP Document') fetchSopDocuments();
        if (deleteTargetType === 'Buyer Folder') { fetchSopBuyers(); fetchSopDocuments(); setSelectedBuyerFolder(null); }
        if (deleteTargetType === 'Fabric Product Category') fetchFabricProductCategories();
        if (deleteTargetType === 'Defect Category') fetchFabricDefectCategories();
        if (deleteTargetType === 'Defect Type') {
          if (selectedDefectCategoryId) fetchFabricDefectTypes(selectedDefectCategoryId);
          fetchFabricDefectCategories();
        }
        setShowDeleteModal(false);
        setItemToDelete(null);
      }
    } catch (error) {
      console.error(`Error deleting ${deleteTargetType}:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFacility = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const facilityData = {
      name: formData.get('facilityName'),
      code: formData.get('facilityCode'),
      address: formData.get('facilityAddress'),
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/facilities/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/facilities`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(facilityData)
      });

      const data = await response.json();
      if (data.success) {
        fetchFacilities();
        handleCloseModal(setShowFacilityModal);
      } else {
        alert(data.message || 'Error saving facility');
      }
    } catch (error) {
      console.error('Error saving facility:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const supplierData = {
      name: formData.get('supplierName'),
      location: formData.get('supplierLocation'),
      gst: formData.get('supplierGst'),
      contact: formData.get('supplierContact'),
      phone: formData.get('supplierPhone'),
      email: formData.get('supplierEmail'),
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/suppliers/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/suppliers`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(supplierData)
      });

      const data = await response.json();
      if (data.success) {
        fetchSuppliers();
        handleCloseModal(setShowSupplierModal);
      } else {
        alert(data.message || 'Error saving supplier');
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFabricImage = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      label: formData.get('imageLabel'),
      category: showAddFabricImageModal,
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-images/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-images`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        fetchFabricImages();
        setShowAddFabricImageModal(null);
        setEditingItem(null);
      } else {
        alert(data.message || 'Error saving image');
      }
    } catch (error) {
      console.error('Error saving fabric image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineImage = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      label: formData.get('imageName'),
      category: showAddLineImageModal,
      process_id: selectedProductionProcess?.id || null,
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-images/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-images`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        fetchLineImages();
        setShowAddLineImageModal(null);
        setEditingItem(null);
      } else {
        alert(data.message || 'Error saving image');
      }
    } catch (error) {
      console.error('Error saving line image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFabricProductCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      title: formData.get('categoryTitle'),
      materials: materialRows.filter((r: { name: string; percent: string | number }) => r.name.trim()).map((r: { name: string; percent: string | number }) => ({ name: r.name, percent: Number(r.percent) })),
      gsm: formData.get('gsm') || null,
      epi: formData.get('epi') || null,
      ppi: formData.get('ppi') || null,
      length: formData.get('length') || null,
      width: formData.get('width') || null,
      selvedge: formData.get('selvedge') || null,
      measurement_tolerance_plus: formData.get('measurementTolerancePlus') || null,
      measurement_tolerance_minus: formData.get('measurementToleranceMinus') || null,
      weight_tolerance_plus: formData.get('weightTolerancePlus') || null,
      weight_tolerance_minus: formData.get('weightToleranceMinus') || null,
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-product-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-product-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFabricProductCategories();
        handleCloseModal(setShowAddProductModal);
      } else {
        alert(data.message || 'Error saving product category');
      }
    } catch (error) {
      console.error('Error saving product category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFinalDefectCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = { name: formData.get('categoryName') };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFinalDefectCategories();
        handleCloseModal(setShowAddFinalDefectCategoryModal);
      } else {
        alert(data.message || 'Error saving category');
      }
    } catch (error) {
      console.error('Error saving final defect category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFinalDefectType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFinalCategoryId) return;
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      category_id: selectedFinalCategoryId,
      code: formData.get('defectCode'),
      name: formData.get('defectName')
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-types/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-defect-types`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFinalDefectTypes(selectedFinalCategoryId);
        handleCloseModal(setShowAddFinalDefectTypeModal);
      } else {
        alert(data.message || 'Error saving defect type');
      }
    } catch (error) {
      console.error('Error saving final defect type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFinalPoChecklist = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      title: formData.get('checklistTitle'),
      description: formData.get('checklistDesc'),
      evidence_required: formData.get('evidenceRequired') === 'on'
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-po-checklists/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-po-checklists`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFinalPoChecklists();
        handleCloseModal(setShowAddFinalChecklistModal);
      } else {
        alert(data.message || 'Error saving checklist item');
      }
    } catch (error) {
      console.error('Error saving final PO checklist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFinalInspectionImage = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      label: formData.get('imageLabel'),
      category: showAddFinalImageModal // 'mandatory' or 'optional'
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-inspection-images/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-inspection-images`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFinalInspectionImages();
        setShowAddFinalImageModal(null);
        setEditingItem(null);
      } else {
        alert(data.message || 'Error saving image label');
      }
    } catch (error) {
      console.error('Error saving final inspection image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFinalProductCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = { name: formData.get('categoryName') };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-product-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/final-product-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFinalProductCategories();
        handleCloseModal(setShowAddFinalProductModal);
      } else {
        alert(data.message || 'Error saving product category');
      }
    } catch (error) {
      console.error('Error saving final product category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineDefectCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      name: formData.get('defectCategoryName'),
      process_id: selectedProductionProcess?.id || null,
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchLineDefectCategories();
        handleCloseModal(setShowAddDefectCategoryModal);
      } else {
        alert(data.message || 'Error saving defect category');
      }
    } catch (error) {
      console.error('Error saving line defect category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineDefectType = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      category_id: selectedLineDefectCategoryId,
      code: formData.get('defectCode'),
      name: formData.get('defectName'),
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-types/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-defect-types`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        if (selectedLineDefectCategoryId) fetchLineDefectTypes(selectedLineDefectCategoryId);
        fetchLineDefectCategories();
        handleCloseModal(setShowAddDefectTypeModal);
      } else {
        alert(data.message || 'Error saving defect type');
      }
    } catch (error) {
      console.error('Error saving line defect type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      name: formData.get('verificationFieldName'),
      process_id: selectedProductionProcess?.id || null,
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-verifications/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-verifications`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchLineVerifications();
        handleCloseModal(setShowAddLineVerificationModal);
      } else {
        alert(data.message || 'Error saving verification field');
      }
    } catch (error) {
      console.error('Error saving line verification:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFabricDefectCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = { name: formData.get('defectCategoryName') };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchFabricDefectCategories();
        handleCloseModal(setShowAddDefectCategoryModal);
      } else {
        alert(data.message || 'Error saving defect category');
      }
    } catch (error) {
      console.error('Error saving defect category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFabricDefectType = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      category_id: selectedDefectCategoryId,
      code: formData.get('defectCode'),
      name: formData.get('defectName'),
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-types/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-defect-types`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        if (selectedDefectCategoryId) fetchFabricDefectTypes(selectedDefectCategoryId);
        fetchFabricDefectCategories();
        handleCloseModal(setShowAddDefectTypeModal);
      } else {
        alert(data.message || 'Error saving defect type');
      }
    } catch (error) {
      console.error('Error saving defect type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFabricChecklist = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      title: formData.get('checklistTitle'),
      description: formData.get('checklistDescription'),
      evidence_required: (e.target as HTMLFormElement).querySelector<HTMLInputElement>('input[name="evidenceRequired"]')?.checked ?? false,
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-checklists/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/fabric-checklists`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        fetchFabricChecklists();
        handleCloseModal(setShowAddChecklistModal);
      } else {
        alert(data.message || 'Error saving checklist');
      }
    } catch (error) {
      console.error('Error saving fabric checklist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineChecklist = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      title: formData.get('checklistTitle'),
      description: formData.get('checklistDescription'),
      evidence_required: (e.target as HTMLFormElement).querySelector<HTMLInputElement>('input[name="evidenceRequired"]')?.checked ?? false,
      process_id: selectedProductionProcess?.id || null,
    };

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-checklists/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-checklists`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        fetchLineChecklists();
        handleCloseModal(setShowAddLineChecklistModal);
      } else {
        alert(data.message || 'Error saving checklist');
      }
    } catch (error) {
      console.error('Error saving line checklist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProductionProcess = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = { name: formData.get('processName') };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/production-processes/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/production-processes`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchProductionProcesses();
        handleCloseModal(setShowAddProductionProcessModal);
      } else {
        alert(data.message || 'Error saving production process');
      }
    } catch (error) {
      console.error('Error saving production process:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveLineProductCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const payload = {
      name: formData.get('categoryName'),
      process_id: selectedProductionProcess?.id || null,
    };
    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-product-categories/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/line-product-categories`;
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        fetchLineProductCategories();
        handleCloseModal(setShowAddLineProductModal);
      } else {
        alert(data.message || 'Error saving product category');
      }
    } catch (error) {
      console.error('Error saving line product category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveContainerChecklistItem = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const itemName = formData.get('itemName');

    setIsLoading(true);
    try {
      const endpointBase = activeContainerTab === 'Points Checklist'
        ? 'points-checklists'
        : 'final-verification-checklists';

      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/${endpointBase}/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/${endpointBase}`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name: itemName })
      });

      const data = await response.json();
      if (data.success) {
        if (activeContainerTab === 'Points Checklist') {
          fetchPointsChecklists();
        } else {
          fetchFinalVerificationChecklists();
        }
        handleCloseModal(setShowAddContainerChecklistItemModal);
      } else {
        alert(data.message || 'Error saving item');
      }
    } catch (error) {
      console.error('Error saving checklist item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveStuffingImage = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const itemLabel = formData.get('itemLabel');

    setIsLoading(true);
    try {
      const url = editingItem
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/stuffing-images/${editingItem.id}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/stuffing-images`;

      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ label: itemLabel, category: showAddContainerImageLabelModal })
      });

      const data = await response.json();
      if (data.success) {
        fetchStuffingImages();
        setShowAddContainerImageLabelModal(null);
        setEditingItem(null);
      } else {
        alert(data.message || 'Error saving image label');
      }
    } catch (error) {
      console.error('Error saving image label:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addMaterialRow = () => {
    setMaterialRows([...materialRows, { name: '', percent: '' }]);
  };

  const removeMaterialRow = (index: number) => {
    setMaterialRows(materialRows.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Master Data Management</h1>
        <p className="text-sm text-slate-500">
          {activeTab === 'Fabric Inspection'
            ? 'Configure fabric parameters, defect classifications, and inspection checklists'
            : activeTab === 'Line Inspection'
              ? 'Configure parameters, processes, and defect classifications for production monitoring'
              : activeTab === 'Final Inspection'
                ? 'Configure product categories, defect classifications, and checklists for final quality checks'
                : activeTab === 'Container Inspection'
                  ? 'Configure parameters, processes, and inspection checklists for shipment logistics'
                  : 'Configure global parameters and primary data sources'}
        </p>
      </div>

      <div className="mb-6 flex border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'Fabric Inspection') {
                setActiveFabricTab('Product Category');
                setFabricView('list');
              } else if (tab === 'Line Inspection') {
                setActiveLineTab('Production Process');
                setLineView('list');
              } else if (tab === 'Final Inspection') {
                setActiveFinalTab('Product Category');
                setFinalView('list');
                setSelectedBuyerFolder(null);
              } else if (tab === 'Container Inspection') {
                setActiveContainerTab('Points Checklist');
              }
            }}
            className={`px-6 py-3 text-sm transition-all relative ${activeTab === tab
              ? 'font-bold text-[#136bce]'
              : 'font-medium text-slate-500 hover:text-slate-700'
              }`}
          >
            {tab}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#136bce]"></div>
            )}
          </button>
        ))}
      </div>

      <div className="space-y-8 pb-12 animate-in fade-in duration-500">
        {activeTab === 'General' && (
          <>
            {/* Facilities Section */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Facilities</h2>
                  <p className="text-xs text-slate-500 font-medium">Manage manufacturing units and site locations</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    ref={facilityImportInputRef}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFacilitiesFile(f); }}
                    className="hidden"
                  />
                  <button
                    onClick={() => facilityImportInputRef.current?.click()}
                    disabled={isImporting === 'Facility'}
                    className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                  >
                    {isImporting === 'Facility' ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                    ) : (
                      <span className="material-symbols-outlined text-[18px]">upload_file</span>
                    )}
                    Import
                  </button>
                  <button
                    onClick={handleExportFacilities}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                    Export
                  </button>
                  <button onClick={() => { setEditingItem(null); setShowFacilityModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Add Facility
                  </button>
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                {isLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                  </div>
                )}
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                    <tr>
                      <th className="px-6 py-4 tracking-wider">Facility Name</th>
                      <th className="px-6 py-4 tracking-wider">Facility Code</th>
                      <th className="px-6 py-4 tracking-wider">Location</th>
                      <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {facilities.length > 0 ? (
                      facilities.map((fac, idx) => (
                        <tr key={fac.id || idx} className="group cursor-pointer hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-semibold text-slate-900 group-hover:text-[#136bce]">{fac.name}</td>
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{fac.code}</td>
                          <td className="px-6 py-4 text-slate-700 font-medium">{fac.address}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(fac, setShowFacilityModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(fac, 'Facility')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                          No facilities found. Click "Add Facility" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Suppliers Section */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Suppliers</h2>
                  <p className="text-xs text-slate-500 font-medium">Manage raw material and service vendors</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    ref={supplierImportInputRef}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportSuppliersFile(f); }}
                    className="hidden"
                  />
                  <button
                    onClick={() => supplierImportInputRef.current?.click()}
                    disabled={isImporting === 'Supplier'}
                    className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                  >
                    {isImporting === 'Supplier' ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                    ) : (
                      <span className="material-symbols-outlined text-[18px]">upload_file</span>
                    )}
                    Import
                  </button>
                  <button
                    onClick={handleExportSuppliers}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                    Export
                  </button>
                  <button onClick={() => { setEditingItem(null); setShowSupplierModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Add Supplier
                  </button>
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                {isLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                  </div>
                )}
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                    <tr>
                      <th className="px-6 py-4 tracking-wider">Supplier Name</th>
                      <th className="px-6 py-4 tracking-wider">GST Number</th>
                      <th className="px-6 py-4 tracking-wider">Location</th>
                      <th className="px-6 py-4 tracking-wider">Contact Person</th>
                      <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {suppliers.length > 0 ? (
                      suppliers.map((sup, idx) => (
                        <tr key={sup.id || idx} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-900 group-hover:text-[#136bce]">{sup.name}</span>
                              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{sup.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{sup.gst || 'N/A'}</td>
                          <td className="px-6 py-4 text-slate-700 font-medium">{sup.location}</td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-slate-700">{sup.contact}</span>
                              <span className="text-[11px] text-slate-400 font-medium">{sup.phone}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(sup, setShowSupplierModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(sup, 'Supplier')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                          No suppliers found. Click "Add Supplier" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'Fabric Inspection' && (
          <>
            {/* Fabric Sub Tabs */}
            <div className="mb-8 flex gap-2 rounded-lg bg-slate-200/50 p-1 w-fit">
              {fabricTabs.map((fTab) => (
                <button
                  key={fTab}
                  onClick={() => {
                    setActiveFabricTab(fTab);
                    setFabricView('list');
                  }}
                  className={`rounded-md px-6 py-2 text-sm transition-all ${activeFabricTab === fTab
                    ? 'bg-white font-bold text-[#136bce] shadow-sm'
                    : 'font-medium text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  {fTab}
                </button>
              ))}
            </div>

            {activeFabricTab === 'Product Category' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Product Categories</h2>
                    <p className="text-xs text-slate-500 font-medium">Define material standards for different fabric types</p>
                  </div>
                  <button onClick={() => { setEditingItem(null); setMaterialRows([{ name: '', percent: 0 }]); setShowAddProductModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Add Product Category
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 tracking-wider">Category Title</th>
                        <th className="px-6 py-4 tracking-wider">Material Composition</th>
                        <th className="px-6 py-4 tracking-wider">Material GSM</th>
                        <th className="px-6 py-4 tracking-wider">Target EPI/PPI</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {fabricProductCategories.length > 0 ? (
                        fabricProductCategories.map((pc: any, idx: number) => (
                          <tr key={pc.id || idx} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-bold text-slate-900">{pc.title}</td>
                            <td className="px-6 py-4 text-slate-700 font-medium">
                              {pc.materials?.length > 0
                                ? pc.materials.map((m: any) => `${m.percent}% ${m.name}`).join(', ')
                                : '—'}
                            </td>
                            <td className="px-6 py-4 text-slate-700 font-medium">{pc.gsm ? `${pc.gsm} GSM` : '—'}</td>
                            <td className="px-6 py-4 font-mono text-xs text-slate-500">
                              {pc.epi || pc.ppi ? `${pc.epi ?? '—'} / ${pc.ppi ?? '—'}` : '—'}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setMaterialRows(pc.materials?.length > 0 ? pc.materials : [{ name: '', percent: '' }]); handleEdit(pc, setShowAddProductModal); }} className="text-slate-400 hover:text-[#136bce] transition-colors">
                                  <span className="material-symbols-outlined text-[18px]">edit</span>
                                </button>
                                <button onClick={() => openDeleteModal(pc, 'Fabric Product Category')} className="text-slate-400 hover:text-red-600 transition-colors">
                                  <span className="material-symbols-outlined text-[18px]">delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No product categories found. Click "Add Product Category" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFabricTab === 'Defect Category & Type' && fabricView === 'list' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Defect Categories & Types</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage defect classifications and their specific underlying types</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={fabricDefectImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFabricDefectsFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => fabricDefectImportInputRef.current?.click()}
                      disabled={isImporting === 'FabricDefects'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                    >
                      {isImporting === 'FabricDefects' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportFabricDefects}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button onClick={() => { setEditingItem(null); setShowAddDefectCategoryModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Category
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-16 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Defect Category</th>
                        <th className="px-6 py-4 tracking-wider">Number of Defect Types</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {fabricDefectCategories.length > 0 ? fabricDefectCategories.map((dc: any, idx: number) => (
                        <tr key={dc.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 text-xs font-bold text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dc.name}</td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-[#136bce]">
                              {dc.defect_types_count} Types
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => {
                                  setSelectedDefectCategory(dc.name);
                                  setSelectedDefectCategoryId(dc.id);
                                  fetchFabricDefectTypes(dc.id);
                                  setFabricView('defect-details');
                                }}
                                className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-700 hover:bg-slate-50 transition-colors uppercase tracking-tight"
                              >
                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                Add/View Defect Types
                              </button>
                              <div className="h-4 w-px bg-slate-200 mx-1"></div>
                              <button onClick={() => handleEdit(dc, setShowAddDefectCategoryModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dc, 'Defect Category')} className="text-slate-400 hover:text-red-500 transition-colors p-1"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic font-medium">No defect categories found. Click "Add New Category" to create one.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFabricTab === 'Defect Category & Type' && fabricView === 'defect-details' && (
              <div>
                <nav className="mb-4 flex items-center gap-2 text-sm">
                  <button
                    onClick={() => setFabricView('list')}
                    className="flex items-center gap-1 text-slate-500 hover:text-[#136bce] transition-colors font-medium"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Back to Defect Category & Type
                  </button>
                </nav>
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 leading-tight">Defect Category: <span className="text-[#136bce]">{selectedDefectCategory}</span></h1>
                    <p className="text-sm text-slate-500 mt-1">Manage specific defect types and their codes for the Fabric Inspection process</p>
                  </div>
                  <button onClick={() => { setEditingItem(null); setShowAddDefectTypeModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[20px]">add</span>
                    Add New Defect
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-1/4 tracking-wider">DCL Code</th>
                        <th className="px-6 py-4 tracking-wider">Defect Type</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {fabricDefectTypes.length > 0 ? fabricDefectTypes.map((dt: any, idx: number) => (
                        <tr key={dt.id || idx} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <code className="rounded bg-slate-100 px-2 py-1 text-xs font-mono font-bold text-slate-600 uppercase">{dt.code}</code>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dt.name}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button onClick={() => handleEdit(dt, setShowAddDefectTypeModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dt, 'Defect Type')} className="text-slate-400 hover:text-red-500 transition-colors p-1"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">No defect types found. Click "Add New Defect" to create one.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFabricTab === 'Checklist' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Checklists</h2>
                    <p className="text-xs text-slate-500 font-medium">Define inspection criteria and evidence requirements</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="file" accept=".xlsx,.xls" ref={fabricChecklistImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFabricChecklistsFile(f); }} className="hidden" />
                    <button onClick={() => fabricChecklistImportInputRef.current?.click()} disabled={isImporting === 'FabricChecklists'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                      {isImporting === 'FabricChecklists' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                      Import
                    </button>
                    <button onClick={handleExportFabricChecklists} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button onClick={() => { setEditingItem(null); setShowAddChecklistModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Checklist
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 tracking-wider">Checklist Title</th>
                        <th className="px-6 py-4 tracking-wider">Description</th>
                        <th className="px-6 py-4 tracking-wider">Evidence Required</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {fabricChecklists.length === 0 && !isLoading && (
                        <tr><td colSpan={4} className="px-6 py-10 text-center text-sm text-slate-400 font-medium">No checklist items found. Add one to get started.</td></tr>
                      )}
                      {fabricChecklists.map((ch) => (
                        <tr key={ch.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-900">{ch.title}</td>
                          <td className="px-6 py-4 text-slate-500 font-medium max-w-xs truncate">{ch.description || '-'}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ch.evidence_required ? 'bg-blue-50 text-[#136bce]' : 'bg-slate-100 text-slate-500'}`}>
                                {ch.evidence_required ? 'Yes' : 'No'}
                              </span>
                              <div className="relative inline-block w-10 align-middle select-none transition duration-200 ease-in">
                                <input checked={ch.evidence_required} readOnly className={`absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer ${ch.evidence_required ? 'right-0 border-[#136bce]' : 'left-0 border-slate-300'}`} type="checkbox" />
                                <label className={`block overflow-hidden h-5 rounded-full cursor-pointer ${ch.evidence_required ? 'bg-[#136bce]' : 'bg-slate-300'}`}></label>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(ch, setShowAddChecklistModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(ch, 'Fabric Checklist')} className="text-slate-400 hover:text-red-600 transition-colors p-1"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFabricTab === 'Inspection Images' && (
              <div className="space-y-12">
                {/* Mandatory Images */}
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Mandatory Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Visual evidence required for every fabric inspection report</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={fabricMandatoryImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFabricImagesFile(f, 'mandatory'); }} className="hidden" />
                      <button onClick={() => fabricMandatoryImageImportInputRef.current?.click()} disabled={isImporting === 'FabricMandatoryImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'FabricMandatoryImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportFabricImages('mandatory')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddFabricImageModal('mandatory'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Mandatory Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {fabricMandatoryImages.length === 0 && !isLoading && (
                          <tr><td colSpan={3} className="px-6 py-10 text-center text-sm text-slate-400 font-medium">No mandatory images found. Add one to get started.</td></tr>
                        )}
                        {fabricMandatoryImages.map((img, idx) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-400">{idx + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddFabricImageModal('mandatory'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Fabric Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Optional Images */}
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Optional Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Visual evidence required for every fabric inspection report</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={fabricOptionalImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFabricImagesFile(f, 'optional'); }} className="hidden" />
                      <button onClick={() => fabricOptionalImageImportInputRef.current?.click()} disabled={isImporting === 'FabricOptionalImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'FabricOptionalImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportFabricImages('optional')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddFabricImageModal('optional'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Optional Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {fabricOptionalImages.length === 0 && !isLoading && (
                          <tr><td colSpan={3} className="px-6 py-10 text-center text-sm text-slate-400 font-medium">No optional images found. Add one to get started.</td></tr>
                        )}
                        {fabricOptionalImages.map((img, idx) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-400">{idx + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddFabricImageModal('optional'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Fabric Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'Line Inspection' && (
          <>
            {/* Line Sub Tabs */}
            <div className="mb-8 flex gap-2 rounded-lg bg-slate-200/50 p-1 w-fit">
              {lineTabs.map((lTab) => (
                <button
                  key={lTab}
                  onClick={() => {
                    setActiveLineTab(lTab);
                    setLineView('list');
                  }}
                  className={`rounded-md px-6 py-2 text-sm transition-all ${activeLineTab === lTab
                    ? 'bg-white font-bold text-[#136bce] shadow-sm'
                    : 'font-medium text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  {lTab}
                </button>
              ))}
            </div>

            {activeLineTab === 'Product Category' && (
              <div>
                <div className="mb-6 flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-[#136bce]">
                      <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#136bce]">Active Production Process</p>
                      <h3 className="text-lg font-bold text-slate-900">{selectedProductionProcess?.name || 'None Selected'}</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveLineTab('Production Process')}
                    className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-[#136bce] transition-all hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                    Change Process
                  </button>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Product Categories</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage product groups for production line inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={lineProductCatImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineProductCategoriesFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => lineProductCatImportInputRef.current?.click()}
                      disabled={!selectedProductionProcess || isImporting === 'LineProductCategories'}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting === 'LineProductCategories' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportLineProductCategories}
                      disabled={!selectedProductionProcess}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddLineProductModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add Product Category
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Category Name</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineProductCategories.filter((pc: any) => selectedProductionProcess && pc.process_id === selectedProductionProcess?.id).length > 0 ? (
                        lineProductCategories.filter((pc: any) => selectedProductionProcess && pc.process_id === selectedProductionProcess?.id).map((pc: any, index: number) => (
                          <tr key={pc.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs">{index + 1}</td>
                            <td className="px-6 py-4 font-bold text-slate-900">{pc.name}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => handleEdit(pc, setShowAddLineProductModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(pc, 'Line Product Category')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic">
                            {selectedProductionProcess ? `No product categories defined for ${selectedProductionProcess.name}` : 'Select a production process to view categories'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeLineTab === 'Defect Category & Type' && lineView === 'list' && (
              <div>
                <div className="mb-6 flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-[#136bce]">
                      <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#136bce]">Active Production Process</p>
                      <h3 className="text-lg font-bold text-slate-900">{selectedProductionProcess?.name || 'None Selected'}</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveLineTab('Production Process')}
                    className="flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-bold text-[#136bce] transition-all hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                    Change Process
                  </button>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Defect Categories & Types</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage defect hierarchy and specific fault types</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={lineDefectImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineDefectsFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => lineDefectImportInputRef.current?.click()}
                      disabled={!selectedProductionProcess || isImporting === 'LineDefects'}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting === 'LineDefects' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportLineDefects}
                      disabled={!selectedProductionProcess}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddDefectCategoryModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Category
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Defect Category</th>
                        <th className="px-6 py-4 tracking-wider">Number of Defect Types</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineDefectCategories.length > 0 ? lineDefectCategories.map((dc, index: number) => (
                        <tr key={dc.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dc.name}</td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-[#136bce] ring-1 ring-inset ring-blue-100">
                              {dc.defect_types_count ?? 0} Types
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end items-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedLineDefectCategory(dc.name);
                                  setSelectedLineDefectCategoryId(dc.id);
                                  fetchLineDefectTypes(dc.id);
                                  setLineView('defect-details');
                                }}
                                className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-700 hover:bg-slate-50 transition-colors uppercase tracking-tight"
                              >
                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                Add/View Defect Types
                              </button>
                              <div className="h-4 w-px bg-slate-200 mx-1"></div>
                              <button onClick={() => handleEdit(dc, setShowAddDefectCategoryModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dc, 'Line Defect Category')} className="text-slate-400 hover:text-red-600 transition-colors p-1"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic font-medium">{selectedProductionProcess ? `No defect categories defined for ${selectedProductionProcess.name}` : 'Select a production process to view defect categories'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeLineTab === 'Defect Category & Type' && lineView === 'defect-details' && (
              <div>
                <nav className="mb-4 flex items-center gap-2 text-sm">
                  <button
                    onClick={() => setLineView('list')}
                    className="flex items-center gap-1 text-slate-500 hover:text-[#136bce] transition-colors font-medium"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Back to Defect Category & Type
                  </button>
                </nav>
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 leading-tight">Defect Category: <span className="text-[#136bce]">{selectedLineDefectCategory}</span></h1>
                    <p className="text-sm text-slate-500 mt-1">Manage specific defect types for the Production monitoring process</p>
                  </div>
                  <button onClick={() => { setEditingItem(null); setShowAddDefectTypeModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[20px]">add</span>
                    Add New Defect
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-1/4 tracking-wider">DCL Code</th>
                        <th className="px-6 py-4 tracking-wider">Defect Type</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineDefectTypes.length > 0 ? lineDefectTypes.map((dt: any) => (
                        <tr key={dt.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <code className="rounded bg-slate-100 px-2 py-1 text-xs font-mono font-bold text-slate-600 uppercase">{dt.code}</code>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dt.name}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(dt, setShowAddDefectTypeModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dt, 'Line Defect Type')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">No defect types found. Click "Add New Defect" to create one.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeLineTab === 'Checklist' && (
              <div>
                <div className="mb-6 flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-[#136bce]">
                      <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#136bce]">Active Production Process</p>
                      <h3 className="text-lg font-bold text-slate-900">{selectedProductionProcess?.name || 'None Selected'}</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveLineTab('Production Process')}
                    className="flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-bold text-[#136bce] transition-all hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                    Change Process
                  </button>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Checklists</h2>
                    <p className="text-xs text-slate-500 font-medium">Standard validation points for quality assurance during line inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={lineChecklistImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineChecklistsFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => lineChecklistImportInputRef.current?.click()}
                      disabled={!selectedProductionProcess || isImporting === 'LineChecklists'}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting === 'LineChecklists' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportLineChecklists}
                      disabled={!selectedProductionProcess}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddLineChecklistModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Checklist
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Checklist Title</th>
                        <th className="px-6 py-4 tracking-wider">Description</th>
                        <th className="px-6 py-4 w-48 tracking-wider">Evidence Required</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineChecklists.length > 0 ? lineChecklists.map((ch, index: number) => (
                        <tr key={ch.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-mono text-xs">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{ch.title}</td>
                          <td className="px-6 py-4 text-slate-500 font-medium">{ch.description}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                <input checked={ch.evidence_required} readOnly className={`absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer ${ch.evidence_required ? 'right-0 border-[#136bce]' : 'left-0 border-slate-300'}`} type="checkbox" />
                                <label className={`block overflow-hidden h-5 rounded-full cursor-pointer ${ch.evidence_required ? 'bg-[#136bce]' : 'bg-slate-300'}`}></label>
                              </div>
                              <span className={`text-[10px] font-bold uppercase tracking-wider ${ch.evidence_required ? 'text-[#136bce]' : 'text-slate-400'}`}>
                                {ch.evidence_required ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(ch, setShowAddLineChecklistModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(ch, 'Line Checklist')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic font-medium">{selectedProductionProcess ? `No checklists defined for ${selectedProductionProcess.name}` : 'Select a production process to view checklists'}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeLineTab === 'Verification' && (
              <div>
                <div className="mb-6 flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-[#136bce]">
                      <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#136bce]">Active Production Process</p>
                      <h3 className="text-lg font-bold text-slate-900">{selectedProductionProcess?.name || 'None Selected'}</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveLineTab('Production Process')}
                    className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-[#136bce] transition-all hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                    Change Process
                  </button>
                </div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Verification Fields</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage specific data fields required for verification during line inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={lineVerificationImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineVerificationsFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => lineVerificationImportInputRef.current?.click()}
                      disabled={!selectedProductionProcess || isImporting === 'LineVerifications'}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting === 'LineVerifications' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportLineVerifications}
                      disabled={!selectedProductionProcess}
                      title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddLineVerificationModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Field
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Field Name</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineVerifications.length > 0 ? lineVerifications.map((f, index: number) => (
                        <tr key={f.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-mono text-xs">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{f.name}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(f, setShowAddLineVerificationModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(f, 'Line Verification')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            {selectedProductionProcess ? `No verification fields defined for ${selectedProductionProcess.name}` : 'Select a production process to view verification fields'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeLineTab === 'Product Images' && (
              <div className="space-y-12">
                <div className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50/50 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-[#136bce]">
                      <span className="material-symbols-outlined text-[24px]">settings_suggest</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#136bce]">Active Production Process</p>
                      <h3 className="text-lg font-bold text-slate-900">{selectedProductionProcess?.name || 'None Selected'}</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveLineTab('Production Process')}
                    className="flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-bold text-[#136bce] transition-all hover:bg-blue-50"
                  >
                    <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                    Change Process
                  </button>
                </div>
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Mandatory Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Visual evidence required for every line inspection report</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        ref={lineMandatoryImageImportInputRef}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineImagesFile(f, 'mandatory'); }}
                        className="hidden"
                      />
                      <button
                        onClick={() => lineMandatoryImageImportInputRef.current?.click()}
                        disabled={!selectedProductionProcess || isImporting === 'LineMandatoryImages'}
                        title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                        className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isImporting === 'LineMandatoryImages' ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                        ) : (
                          <span className="material-symbols-outlined text-[18px]">upload_file</span>
                        )}
                        Import
                      </button>
                      <button
                        onClick={() => handleExportLineImages('mandatory')}
                        disabled={!selectedProductionProcess}
                        title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddLineImageModal('mandatory'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Mandatory Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {lineMandatoryImages.length > 0 ? lineMandatoryImages.map((img: any, index: number) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs">{index + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddLineImageModal('mandatory'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Line Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">{selectedProductionProcess ? `No mandatory images defined for ${selectedProductionProcess.name}` : 'Select a production process to view images'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Optional Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Additional visual contexts that can be uploaded if relevant</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        ref={lineOptionalImageImportInputRef}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportLineImagesFile(f, 'optional'); }}
                        className="hidden"
                      />
                      <button
                        onClick={() => lineOptionalImageImportInputRef.current?.click()}
                        disabled={!selectedProductionProcess || isImporting === 'LineOptionalImages'}
                        title={!selectedProductionProcess ? 'Select a Production Process first' : 'Import .xlsx'}
                        className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isImporting === 'LineOptionalImages' ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                        ) : (
                          <span className="material-symbols-outlined text-[18px]">upload_file</span>
                        )}
                        Import
                      </button>
                      <button
                        onClick={() => handleExportLineImages('optional')}
                        disabled={!selectedProductionProcess}
                        title={!selectedProductionProcess ? 'Select a Production Process first' : 'Export current process to .xlsx'}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddLineImageModal('optional'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Optional Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {lineOptionalImages.length > 0 ? lineOptionalImages.map((img: any, index: number) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs">{index + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddLineImageModal('optional'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Line Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">{selectedProductionProcess ? `No optional images defined for ${selectedProductionProcess.name}` : 'Select a production process to view images'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeLineTab === 'Production Process' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Production Processes</h2>
                    <p className="text-xs text-slate-500 font-medium">Define sequential steps in the manufacturing line</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={productionProcessImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportProductionProcessesFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => productionProcessImportInputRef.current?.click()}
                      disabled={isImporting === 'ProductionProcesses'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isImporting === 'ProductionProcesses' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportProductionProcesses}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddProductionProcessModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add Production Process
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Process Name</th>
                        <th className="px-6 py-4 tracking-wider">Selection</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {productionProcesses.map((process, index: number) => (
                        <tr key={process.id} className={`group transition-colors ${selectedProductionProcess?.id === process.id ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{process.name}</td>
                          <td className="px-6 py-4">
                            {selectedProductionProcess?.id === process.id ? (
                              <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#136bce] px-3 py-1.5 text-[10px] font-bold text-white uppercase tracking-wider shadow-sm shadow-blue-500/20">
                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                Active Process
                              </span>
                            ) : (
                              <button
                                onClick={() => { setSelectedProductionProcess(process); setSelectedLineProductCategory(null); }}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-[#136bce] uppercase tracking-wider transition-all shadow-sm"
                              >
                                <span className="material-symbols-outlined text-[16px]">ads_click</span>
                                Select Process
                              </button>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(process, setShowAddProductionProcessModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(process, 'Production Process')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </>
        )}

        {activeTab === 'Final Inspection' && (
          <>
            {/* Final Inspection Sub Tabs */}
            <div className="mb-8 flex gap-2 rounded-lg bg-slate-200/50 p-1 w-fit">
              {finalTabs.map((fTab) => (
                <button
                  key={fTab}
                  onClick={() => {
                    setActiveFinalTab(fTab);
                    setFinalView('list');
                    setSelectedBuyerFolder(null);
                  }}
                  className={`rounded-md px-6 py-2 text-sm transition-all ${
                    activeFinalTab === fTab
                      ? 'bg-white font-bold text-[#136bce] shadow-sm'
                      : 'font-medium text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {fTab}
                </button>
              ))}
            </div>

            {activeFinalTab === 'Product Category' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Product Categories</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage product groups for final inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="file" accept=".xlsx,.xls" ref={finalProductCatImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalProductCategoriesFile(f); }} className="hidden" />
                    <button onClick={() => finalProductCatImportInputRef.current?.click()} disabled={isImporting === 'FinalProductCategories'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                      {isImporting === 'FinalProductCategories' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                      Import
                    </button>
                    <button onClick={handleExportFinalProductCategories} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddFinalProductModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add Product Category
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Category Name</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {finalProductCategories.length > 0 ? finalProductCategories.map((pc, index: number) => (
                        <tr key={pc.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{pc.name}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(pc, setShowAddFinalProductModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(pc, 'Final Product Category')} className="text-slate-400 hover:text-red-600 transition-colors p-1"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No product categories found for final inspection.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFinalTab === 'Defect Category & Type' && finalView === 'list' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Defect Categories &amp; Types</h2>
                    <p className="text-xs text-slate-500 font-medium">Manage defect classifications for final inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      ref={finalDefectImportInputRef}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalDefectsFile(f); }}
                      className="hidden"
                    />
                    <button
                      onClick={() => finalDefectImportInputRef.current?.click()}
                      disabled={isImporting === 'FinalDefects'}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                    >
                      {isImporting === 'FinalDefects' ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                      ) : (
                        <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      )}
                      Import
                    </button>
                    <button
                      onClick={handleExportFinalDefects}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button onClick={() => { setEditingItem(null); setShowAddFinalDefectCategoryModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Category
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-16 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Defect Category</th>
                        <th className="px-6 py-4 tracking-wider">Number of Defect Types</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {finalDefectCategories.length > 0 ? finalDefectCategories.map((dc, idx) => (
                        <tr key={dc.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 text-xs font-bold text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dc.name}</td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-[#136bce]">
                              {dc.defect_types_count || 0} Types
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => {
                                  setSelectedFinalCategoryId(dc.id);
                                  setSelectedFinalCategoryName(dc.name);
                                  fetchFinalDefectTypes(dc.id);
                                  setFinalView('defect-details');
                                }}
                                className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-bold text-slate-700 hover:bg-slate-50 transition-colors uppercase tracking-tight"
                              >
                                <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                Add/View Defect Types
                              </button>
                              <div className="h-4 w-px bg-slate-200 mx-1"></div>
                              <button onClick={() => handleEdit(dc, setShowAddFinalDefectCategoryModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[18px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dc, 'Final Defect Category')} className="text-slate-400 hover:text-red-500 transition-colors p-1"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={4} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No defect categories found for final inspection.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFinalTab === 'Defect Category & Type' && finalView === 'defect-details' && (
              <div>
                <nav className="mb-4 flex items-center gap-2 text-sm">
                  <button
                    onClick={() => setFinalView('list')}
                    className="flex items-center gap-1 text-slate-500 hover:text-[#136bce] transition-colors font-medium"
                  >
                    <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                    Back to Defect Category &amp; Type
                  </button>
                </nav>
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 leading-tight">Defect Category: <span className="text-[#136bce]">{selectedFinalCategoryName}</span></h1>
                    <p className="text-sm text-slate-500 mt-1">Manage specific defect types for Final Inspection</p>
                  </div>
                  <button onClick={() => { setEditingItem(null); setShowAddFinalDefectTypeModal(true); }} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700">
                    <span className="material-symbols-outlined text-[20px]">add</span>
                    Add New Defect
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-1/4 tracking-wider">DCL Code</th>
                        <th className="px-6 py-4 tracking-wider">Defect Type</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {finalDefectTypes.length > 0 ? finalDefectTypes.map((dt, idx) => (
                        <tr key={dt.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4">
                            <code className="rounded bg-slate-100 px-2 py-1 text-xs font-mono font-bold text-slate-600 uppercase">{dt.code}</code>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-900">{dt.name}</td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button onClick={() => handleEdit(dt, setShowAddFinalDefectTypeModal)} className="text-slate-400 hover:text-[#136bce] transition-colors p-1"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(dt, 'Final Defect Type')} className="text-slate-400 hover:text-red-500 transition-colors p-1"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No defect types found for this category.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFinalTab === 'PO Checklist' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">PO Checklists</h2>
                    <p className="text-xs text-slate-500 font-medium">Standard validation points for final inspection</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="file" accept=".xlsx,.xls" ref={finalPoChecklistImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalPoChecklistsFile(f); }} className="hidden" />
                    <button onClick={() => finalPoChecklistImportInputRef.current?.click()} disabled={isImporting === 'FinalPoChecklists'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                      {isImporting === 'FinalPoChecklists' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                      Import
                    </button>
                    <button onClick={handleExportFinalPoChecklists} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddFinalChecklistModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add New Checklist
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Checklist Title</th>
                        <th className="px-6 py-4 tracking-wider">Description</th>
                        <th className="px-6 py-4 w-48 tracking-wider">Evidence Required</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {finalChecklists.length > 0 ? finalChecklists.map((ch, index: number) => (
                        <tr key={ch.id} className="group hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                          <td className="px-6 py-4 font-bold text-slate-900">{ch.title}</td>
                          <td className="px-6 py-4 text-slate-500 font-medium">{ch.description}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                <input checked={ch.evidence_required} readOnly className={`absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer ${ch.evidence_required ? 'right-0 border-[#136bce]' : 'left-0 border-slate-300'}`} type="checkbox" />
                                <label className={`block overflow-hidden h-5 rounded-full cursor-pointer ${ch.evidence_required ? 'bg-[#136bce]' : 'bg-slate-300'}`}></label>
                              </div>
                              <span className={`text-[10px] font-bold uppercase tracking-wider ${ch.evidence_required ? 'text-[#136bce]' : 'text-slate-400'}`}>
                                {ch.evidence_required ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-3">
                              <button onClick={() => handleEdit(ch, setShowAddFinalChecklistModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                              <button onClick={() => openDeleteModal(ch, 'Final PO Checklist')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No checklist items found for final inspection.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeFinalTab === 'Product Images' && (
              <div className="space-y-12">
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Mandatory Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Visual evidence required for every final inspection report</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={finalMandatoryImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalImagesFile(f, 'mandatory'); }} className="hidden" />
                      <button onClick={() => finalMandatoryImageImportInputRef.current?.click()} disabled={isImporting === 'FinalMandatoryImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'FinalMandatoryImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportFinalImages('mandatory')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddFinalImageModal('mandatory'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Mandatory Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {finalMandatoryImages.length > 0 ? finalMandatoryImages.map((img, index: number) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddFinalImageModal('mandatory'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Final Inspection Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={3} className="px-6 py-10 text-center text-slate-400 italic font-medium">
                              No mandatory images defined.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Optional Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Additional visual contexts that can be uploaded if relevant</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={finalOptionalImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalImagesFile(f, 'optional'); }} className="hidden" />
                      <button onClick={() => finalOptionalImageImportInputRef.current?.click()} disabled={isImporting === 'FinalOptionalImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'FinalOptionalImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportFinalImages('optional')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddFinalImageModal('optional'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Optional Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20">#</th>
                          <th className="px-6 py-4">Image Name</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {finalOptionalImages.length > 0 ? finalOptionalImages.map((img, index: number) => (
                          <tr key={img.id} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-500">{index + 1}</td>
                            <td className="px-6 py-4 font-medium text-slate-900">{img.label}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => { setEditingItem(img); setShowAddFinalImageModal('optional'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(img, 'Final Inspection Image')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={3} className="px-6 py-10 text-center text-slate-400 italic font-medium">
                              No optional images defined.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeFinalTab === 'SOP Documents' && (
              <div>
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {selectedBuyerFolder && (
                      <button
                        onClick={() => setSelectedBuyerFolder(null)}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
                      >
                        <span className="material-symbols-outlined">arrow_back</span>
                      </button>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">
                        {selectedBuyerFolder ? `${selectedBuyerFolder.name} SOPs` : 'SOP Documents'}
                      </h2>
                      <p className="text-xs text-slate-500 font-medium">
                        {selectedBuyerFolder
                          ? `Viewing standard operating procedures for ${selectedBuyerFolder.name}`
                          : 'Select a buyer folder to view or upload SOP documents'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {!selectedBuyerFolder && (
                      <button
                        onClick={() => { setEditingItem(null); setShowAddBuyerModal(true); }}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50"
                      >
                        <span className="material-symbols-outlined text-[18px]">create_new_folder</span>
                        New Buyer Folder
                      </button>
                    )}
                    <button
                      onClick={handleZipAllSOPFiles}
                      disabled={isZippingFiles || sopDocuments.length === 0}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isZippingFiles ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div>
                          Zipping...
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-[18px]">folder_zip</span>
                          Zip All Files
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddSOPModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">upload_file</span>
                      Upload SOP
                    </button>
                  </div>
                </div>

                {!selectedBuyerFolder ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {buyers.map((buyer) => {
                      const docCount = sopDocuments.filter(doc => doc.buyer_id === buyer.id).length;
                      return (
                        <div
                          key={buyer.id}
                          onClick={() => setSelectedBuyerFolder(buyer)}
                          className="group relative flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-center transition-all hover:border-[#136bce] hover:shadow-xl hover:shadow-blue-500/5 cursor-pointer"
                        >
                          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-50 text-[#136bce] transition-transform group-hover:scale-110">
                            <span className="material-symbols-outlined text-[48px]">folder</span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900">{buyer.name}</h3>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{docCount} Documents</p>
                          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); openDeleteModal(buyer, 'Buyer Folder'); }}
                              className="text-slate-300 hover:text-red-500"
                            >
                              <span className="material-symbols-outlined text-[20px]">delete</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 tracking-wider">Document Title</th>
                          <th className="px-6 py-4 tracking-wider">File Name</th>
                          <th className="px-6 py-4 tracking-wider">Upload Date</th>
                          <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sopDocuments.filter(doc => doc.buyer_id === selectedBuyerFolder?.id).length > 0 ? (
                          sopDocuments.filter(doc => doc.buyer_id === selectedBuyerFolder?.id).map((doc) => (
                            <tr key={doc.id} className="group hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 text-slate-700 font-bold">{doc.title}</td>
                              <td className="px-6 py-4">
                                <button onClick={async () => {
                                  try {
                                    const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents/${doc.id}/download`, {
                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    if (!res.ok) return alert('Failed to load preview');
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    setSopPreviewDoc({ name: doc.file_name || 'document', url, mime: res.headers.get('content-type') || '', blob });
                                  } catch (err) { console.error('Preview error:', err); }
                                }} className="flex items-center gap-2 text-[#136bce] hover:text-[#0e52a0] transition-colors cursor-pointer">
                                  <span className="material-symbols-outlined text-[18px]">description</span>
                                  <span className="text-xs font-mono font-medium underline">{doc.file_name}</span>
                                </button>
                              </td>
                              <td className="px-6 py-4 text-slate-500 text-xs font-medium">{formatDate(doc.created_at)}</td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-3">
                                  <button onClick={async () => {
                                    try {
                                      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/sop/documents/${doc.id}/download`, {
                                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                      });
                                      if (!res.ok) return alert('Download failed');
                                      const blob = await res.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = doc.file_name || 'document';
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    } catch (err) { console.error('Download error:', err); }
                                  }} className="text-slate-400 hover:text-[#136bce] transition-colors p-1" title="Download"><span className="material-symbols-outlined text-[18px]">download</span></button>
                                  <button onClick={() => openDeleteModal(doc, 'SOP Document')} className="text-slate-400 hover:text-red-600 transition-colors p-1"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={4} className="px-6 py-12 text-center">
                              <div className="flex flex-col items-center gap-2 text-slate-400">
                                <span className="material-symbols-outlined text-[48px]">folder_open</span>
                                <p className="font-medium">No documents found in this folder</p>
                                <button
                                  onClick={() => { setEditingItem(null); setShowAddSOPModal(true); }}
                                  className="mt-2 text-sm font-bold text-[#136bce] hover:underline"
                                >
                                  Upload the first SOP
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'Container Inspection' && (
          <>
            {/* Container Sub Tabs */}
            <div className="mb-8 flex gap-2 rounded-lg bg-slate-200/50 p-1 w-fit">
              {containerTabs.map((cTab) => (
                <button
                  key={cTab}
                  onClick={() => {
                    setActiveContainerTab(cTab);
                  }}
                  className={`rounded-md px-6 py-2 text-sm transition-all ${activeContainerTab === cTab
                    ? 'bg-white font-bold text-[#136bce] shadow-sm'
                    : 'font-medium text-slate-600 hover:bg-slate-100'
                    }`}
                >
                  {cTab}
                </button>
              ))}
            </div>

            {activeContainerTab === 'Points Checklist' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Points Checklist</h2>
                    <p className="text-xs text-slate-500 font-medium">Standard structural inspection points for export containers</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="file" accept=".xlsx,.xls" ref={pointsChecklistImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportPointsChecklistsFile(f); }} className="hidden" />
                    <button onClick={() => pointsChecklistImportInputRef.current?.click()} disabled={isImporting === 'PointsChecklists'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                      {isImporting === 'PointsChecklists' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                      Import
                    </button>
                    <button onClick={handleExportPointsChecklists} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddContainerChecklistItemModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add Checklist Item
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                  {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                    </div>
                  )}
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Checklist Item</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pointsChecklists.length > 0 ? (
                        pointsChecklists.map((item, idx) => (
                          <tr key={item.id || idx} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                            <td className="px-6 py-4 font-bold text-slate-900">{item.name}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => handleEdit(item, setShowAddContainerChecklistItemModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(item, 'Points Checklist')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No checklist items found. Click "Add Checklist Item" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeContainerTab === 'Final Verification Checklist' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Final Verification Checklist</h2>
                    <p className="text-xs text-slate-500 font-medium">Secondary confirmation items for post-inspection final validation</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="file" accept=".xlsx,.xls" ref={finalVerificationChecklistImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFinalVerificationChecklistsFile(f); }} className="hidden" />
                    <button onClick={() => finalVerificationChecklistImportInputRef.current?.click()} disabled={isImporting === 'FinalVerificationChecklists'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                      {isImporting === 'FinalVerificationChecklists' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                      Import
                    </button>
                    <button onClick={handleExportFinalVerificationChecklists} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Export
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowAddContainerChecklistItemModal(true); }}
                      className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                    >
                      <span className="material-symbols-outlined text-[18px]">add</span>
                      Add Checklist Item
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                  {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                    </div>
                  )}
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                      <tr>
                        <th className="px-6 py-4 w-20 tracking-wider">#</th>
                        <th className="px-6 py-4 tracking-wider">Checklist Item</th>
                        <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {finalVerificationChecklists.length > 0 ? (
                        finalVerificationChecklists.map((item, idx) => (
                          <tr key={item.id || idx} className="group hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-mono text-xs text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                            <td className="px-6 py-4 font-bold text-slate-900">{item.name}</td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-3">
                                <button onClick={() => handleEdit(item, setShowAddContainerChecklistItemModal)} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                <button onClick={() => openDeleteModal(item, 'Verification Item')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                            No verification checklist items found. Click "Add Checklist Item" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeContainerTab === 'Stuffing Images' && (
              <div className="space-y-12">
                {/* Mandatory Images */}
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Mandatory Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Standard image labels required for container stuffing documentation</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={stuffingMandatoryImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportStuffingImagesFile(f, 'mandatory'); }} className="hidden" />
                      <button onClick={() => stuffingMandatoryImageImportInputRef.current?.click()} disabled={isImporting === 'StuffingMandatoryImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'StuffingMandatoryImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportStuffingImages('mandatory')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddContainerImageLabelModal('mandatory'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Mandatory Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                    {isLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                      </div>
                    )}
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20 tracking-wider">#</th>
                          <th className="px-6 py-4 tracking-wider">Image Label</th>
                          <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stuffingMandatoryImages.length > 0 ? (
                          stuffingMandatoryImages.map((item, idx) => (
                            <tr key={item.id || idx} className="group hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 font-mono text-xs text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                              <td className="px-6 py-4 font-bold text-slate-900">{item.label}</td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-3">
                                  <button onClick={() => { setEditingItem(item); setShowAddContainerImageLabelModal('mandatory'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                  <button onClick={() => openDeleteModal(item, 'Image Label')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                              No mandatory image labels found. Click "Add Mandatory Image" to create one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Optional Images */}
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">Optional Images</h2>
                      <p className="text-xs text-slate-500 font-medium">Additional image labels that can be captured if relevant during stuffing</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".xlsx,.xls" ref={stuffingOptionalImageImportInputRef} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportStuffingImagesFile(f, 'optional'); }} className="hidden" />
                      <button onClick={() => stuffingOptionalImageImportInputRef.current?.click()} disabled={isImporting === 'StuffingOptionalImages'} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50">
                        {isImporting === 'StuffingOptionalImages' ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent"></div> : <span className="material-symbols-outlined text-[18px]">upload_file</span>}
                        Import
                      </button>
                      <button onClick={() => handleExportStuffingImages('optional')} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:border-slate-300">
                        <span className="material-symbols-outlined text-[18px]">download</span>
                        Export
                      </button>
                      <button
                        onClick={() => { setEditingItem(null); setShowAddContainerImageLabelModal('optional'); }}
                        className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-sm font-medium text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-700"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        Add Optional Image
                      </button>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative min-h-[200px]">
                    {isLoading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
                      </div>
                    )}
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-xs uppercase font-semibold tracking-wider text-slate-500">
                        <tr>
                          <th className="px-6 py-4 w-20 tracking-wider">#</th>
                          <th className="px-6 py-4 tracking-wider">Image Label</th>
                          <th className="px-6 py-4 text-right tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {stuffingOptionalImages.length > 0 ? (
                          stuffingOptionalImages.map((item, idx) => (
                            <tr key={item.id || idx} className="group hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 font-mono text-xs text-slate-400">{String(idx + 1).padStart(2, '0')}</td>
                              <td className="px-6 py-4 font-bold text-slate-900">{item.label}</td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-3">
                                  <button onClick={() => { setEditingItem(item); setShowAddContainerImageLabelModal('optional'); }} className="text-slate-400 hover:text-[#136bce] transition-colors"><span className="material-symbols-outlined text-[20px]">edit</span></button>
                                  <button onClick={() => openDeleteModal(item, 'Image Label')} className="text-slate-400 hover:text-red-600 transition-colors"><span className="material-symbols-outlined text-[20px]">delete</span></button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className="px-6 py-12 text-center text-slate-400 italic font-medium">
                              No optional image labels found. Click "Add Optional Image" to create one.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}


      </div>

      {/* MODALS */}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)}></div>
          <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200 border border-slate-100 p-8 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
              <span className="material-symbols-outlined text-[32px]">delete_forever</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Confirm Deletion</h3>
            <p className="text-slate-500 text-sm mb-8 leading-relaxed">
              Are you sure you want to permanently delete the {deleteTargetType.toLowerCase()}: <br />
              <span className="font-bold text-slate-800 break-words">"{itemToDelete?.name || itemToDelete?.title || itemToDelete?.item || itemToDelete?.label || 'this item'}"</span>? <br />
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isLoading}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 shadow-md shadow-red-200 transition-all active:scale-95 disabled:opacity-70"
                onClick={confirmDelete}
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Facility Modal */}
      {showFacilityModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => handleCloseModal(setShowFacilityModal)}></div>
          <div className="relative w-full max-lg overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined text-[24px]">factory</span></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Facility' : 'Add Facility'}</h3>
                  <p className="text-xs text-slate-500">{editingItem ? 'Update details for the existing facility' : 'Enter details for the new facility'}</p>
                </div>
              </div>
              <button onClick={() => handleCloseModal(setShowFacilityModal)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <div className="px-6 py-6">
              <form id="facilityForm" onSubmit={handleSaveFacility} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Facility Name</label>
                  <input name="facilityName" defaultValue={editingItem?.name} required className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]" placeholder="e.g. Dhaka Main Unit" type="text" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Facility Code</label>
                  <input name="facilityCode" defaultValue={editingItem?.code} required className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-mono font-bold placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce]" placeholder="e.g. FAC-DK-001" type="text" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Address</label>
                  <textarea name="facilityAddress" defaultValue={editingItem?.address} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] resize-none" placeholder="Enter complete facility address..." rows={3}></textarea>
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button disabled={isLoading} onClick={() => handleCloseModal(setShowFacilityModal)} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-all">Cancel</button>
              <button
                form="facilityForm"
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-bold text-white shadow-md hover:bg-blue-700 disabled:opacity-70 transition-all active:scale-[0.98]"
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Facility' : 'Save Facility'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => handleCloseModal(setShowSupplierModal)}></div>
          <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined">{editingItem ? 'edit' : 'person_add'}</span></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Supplier' : 'Add Supplier'}</h3>
                  <p className="text-xs text-slate-500">{editingItem ? 'Update details for the existing supplier' : 'Enter details for the new supplier'}</p>
                </div>
              </div>
              <button onClick={() => handleCloseModal(setShowSupplierModal)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 transition-colors"><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="px-6 py-6">
              <form id="supplierForm" onSubmit={handleSaveSupplier} className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Supplier Name</label>
                  <input name="supplierName" defaultValue={editingItem?.name} required className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold transition-all focus:border-[#136bce] focus:ring-[#136bce]" placeholder="e.g. Global Textiles Ltd." type="text" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Location</label>
                  <input name="supplierLocation" defaultValue={editingItem?.location} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold transition-all focus:border-[#136bce] focus:ring-[#136bce]" placeholder="City, Country" type="text" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">GST Number</label>
                  <input name="supplierGst" defaultValue={editingItem?.gst} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-mono font-bold transition-all focus:border-[#136bce] focus:ring-[#136bce]" placeholder="GSTIN-123456789" type="text" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Contact Person</label>
                  <input name="supplierContact" defaultValue={editingItem?.contact} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold transition-all focus:border-[#136bce]" placeholder="Full Name" type="text" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phone Number</label>
                  <input name="supplierPhone" defaultValue={editingItem?.phone} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold transition-all focus:border-[#136bce]" placeholder="+91 00000 00000" type="tel" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Email Address</label>
                  <input name="supplierEmail" defaultValue={editingItem?.email} className="w-full rounded-lg border-slate-200 px-4 py-2.5 text-sm font-bold transition-all focus:border-[#136bce]" placeholder="supplier@example.com" type="email" />
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button disabled={isLoading} onClick={() => handleCloseModal(setShowSupplierModal)} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
              <button
                form="supplierForm"
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-bold text-white shadow-md hover:bg-blue-700 disabled:opacity-70 transition-all active:scale-[0.98]"
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Supplier' : 'Save Supplier'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Line Product Image Modal (Mandatory/Optional) */}
      {showAddLineImageModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md scale-100 transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-8 py-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]">
                  <span className="material-symbols-outlined text-[24px]">
                    {showAddLineImageModal === 'mandatory' ? 'image_search' : 'add_photo_alternate'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {editingItem ? 'Edit' : 'Add'} {showAddLineImageModal === 'mandatory' ? 'Mandatory' : 'Optional'} Image
                  </h3>
                  <p className="text-xs font-medium text-slate-500 leading-tight">
                    {editingItem ? 'Update the image requirement label' : `Define a new ${showAddLineImageModal === 'mandatory' ? 'mandatory' : 'optional'} image requirement`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleCloseModal(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleSaveLineImage}>
            <div className="px-8 py-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="imageName">Image Name</label>
                  <div className="relative">
                    <input
                      className="w-full rounded-xl border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-[#136bce] focus:ring-2 focus:ring-[#136bce]/10 transition-all font-bold"
                      id="imageName"
                      name="imageName"
                      defaultValue={editingItem?.label}
                      placeholder="e.g., Custom Tag, Special Packaging"
                      type="text"
                      required
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400 font-medium">This name will appear as a label for the image upload field during inspection.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-8 py-5 rounded-b-2xl">
              <button
                type="button"
                onClick={() => handleCloseModal(null)}
                className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-60"
              >
                <span>{editingItem ? 'Update' : 'Save'}</span>
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Product Category Modal (Fabric) - Refined design matching the Image/HTML request */}
      {showAddProductModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col transition-all animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-8 py-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">
                  {editingItem ? 'Edit Product Category' : 'Add Product Category'}
                </h2>
                <p className="text-xs font-medium text-slate-500">Define fabric specifications and quality parameters</p>
              </div>
              <button type="button" onClick={() => handleCloseModal(setShowAddProductModal)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100 transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <form onSubmit={handleSaveFabricProductCategory} className="flex flex-col flex-1 overflow-hidden">
              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto custom-scrollbar px-8 py-6 space-y-8">
                {/* Product Category Title Section */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product Category Title</label>
                  <input
                    name="categoryTitle"
                    defaultValue={editingItem?.title}
                    required
                    className="rounded-lg border-slate-200 bg-slate-50 text-sm focus:border-[#136bce] focus:ring-[#136bce] transition-all"
                    placeholder="e.g. Stretch Denim 12oz"
                    type="text"
                  />
                </div>

                {/* Material Composition Section */}
                <section>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <span className="material-symbols-outlined text-[18px] text-[#136bce]">science</span>
                      Material Composition
                    </h3>
                    <button type="button" onClick={addMaterialRow} className="text-xs font-bold text-[#136bce] hover:underline flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">add</span> Add Material
                    </button>
                  </div>
                  <div className="space-y-3">
                    {materialRows.map((row: { name: string; percent: string | number }, idx: number) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex-1">
                          <input
                            value={row.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const u = [...materialRows]; u[idx] = { ...u[idx], name: e.target.value }; setMaterialRows(u as { name: string; percent: string | number }[]); }}
                            className="w-full rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]"
                            placeholder="Material Name"
                            type="text"
                          />
                        </div>
                        <div className="w-24 relative">
                          <input
                            value={row.percent}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const u = [...materialRows]; u[idx] = { ...u[idx], percent: e.target.value }; setMaterialRows(u); }}
                            className="w-full rounded-lg border-slate-200 bg-white text-sm pr-7 focus:border-[#136bce] focus:ring-[#136bce]"
                            placeholder="0"
                            type="number"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400">%</span>
                        </div>
                        <button type="button" onClick={() => removeMaterialRow(idx)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                          <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Ends & Picks Section */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4">
                    <span className="material-symbols-outlined text-[18px] text-[#136bce]">texture</span>
                    Ends & Picks
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Material GSM</label>
                      <input name="gsm" defaultValue={editingItem?.gsm ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0.00" type="number" step="0.01" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Target EPI</label>
                      <input name="epi" defaultValue={editingItem?.epi ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0" type="number" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Target PPI</label>
                      <input name="ppi" defaultValue={editingItem?.ppi ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0" type="number" />
                    </div>
                  </div>
                </section>

                {/* Default Measurements Section */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4">
                    <span className="material-symbols-outlined text-[18px] text-[#136bce]">straighten</span>
                    Default Measurements
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-slate-500">Length (m)</label>
                      <input name="length" defaultValue={editingItem?.length ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0.00" type="number" step="0.01" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-slate-500">Width (in)</label>
                      <input name="width" defaultValue={editingItem?.width ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0.00" type="number" step="0.01" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-slate-500">Selvedge (in)</label>
                      <input name="selvedge" defaultValue={editingItem?.selvedge ?? ''} className="rounded-lg border-slate-200 bg-white text-sm focus:border-[#136bce] focus:ring-[#136bce]" placeholder="0.00" type="number" step="0.01" />
                    </div>
                  </div>
                </section>

                {/* Tolerances Section */}
                <section>
                  <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-4">
                    <span className="material-symbols-outlined text-[18px] text-[#136bce]">rule</span>
                    Tolerances
                  </h3>
                  <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">Measurement Tolerance (%)</label>
                      <div className="flex items-stretch shadow-sm rounded-lg overflow-hidden border border-slate-200 focus-within:border-[#136bce] focus-within:ring-1 focus-within:ring-[#136bce] transition-all">
                        <div className="flex-1 relative flex items-center">
                          <div className="absolute left-2.5 flex items-center justify-center w-5 h-5 rounded bg-green-50 text-green-600">
                            <span className="text-sm font-bold leading-none">+</span>
                          </div>
                          <input name="measurementTolerancePlus" defaultValue={editingItem?.measurement_tolerance_plus ?? ''} className="w-full border-none bg-white text-sm pl-9 pr-3 py-2.5 focus:ring-0" placeholder="0" type="number" step="0.01" />
                        </div>
                        <div className="w-px bg-slate-100 my-2"></div>
                        <div className="flex-1 relative flex items-center">
                          <div className="absolute left-2.5 flex items-center justify-center w-5 h-5 rounded bg-red-50 text-red-600">
                            <span className="text-sm font-bold leading-none">−</span>
                          </div>
                          <input name="measurementToleranceMinus" defaultValue={editingItem?.measurement_tolerance_minus ?? ''} className="w-full border-none bg-white text-sm pl-9 pr-3 py-2.5 focus:ring-0" placeholder="0" type="number" step="0.01" />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block">Weight Tolerance (%)</label>
                      <div className="flex items-stretch shadow-sm rounded-lg overflow-hidden border border-slate-200 focus-within:border-[#136bce] focus-within:ring-1 focus-within:ring-[#136bce] transition-all">
                        <div className="flex-1 relative flex items-center">
                          <div className="absolute left-2.5 flex items-center justify-center w-5 h-5 rounded bg-green-50 text-green-600">
                            <span className="text-sm font-bold leading-none">+</span>
                          </div>
                          <input name="weightTolerancePlus" defaultValue={editingItem?.weight_tolerance_plus ?? ''} className="w-full border-none bg-white text-sm pl-9 pr-3 py-2.5 focus:ring-0" placeholder="0" type="number" step="0.01" />
                        </div>
                        <div className="w-px bg-slate-100 my-2"></div>
                        <div className="flex-1 relative flex items-center">
                          <div className="absolute left-2.5 flex items-center justify-center w-5 h-5 rounded bg-red-50 text-red-600">
                            <span className="text-sm font-bold leading-none">−</span>
                          </div>
                          <input name="weightToleranceMinus" defaultValue={editingItem?.weight_tolerance_minus ?? ''} className="w-full border-none bg-white text-sm pl-9 pr-3 py-2.5 focus:ring-0" placeholder="0" type="number" step="0.01" />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-8 py-4">
                <button type="button" onClick={() => handleCloseModal(setShowAddProductModal)} className="rounded-lg px-6 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:bg-slate-100">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  {editingItem ? 'Update Category' : 'Save Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Line Verification Field Modal */}
      {showAddLineVerificationModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Verification Field' : 'Add Verification Field'}</h3>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-100 text-[#136bce] text-[10px] font-bold">
                    <span className="material-symbols-outlined text-[12px]">verified</span>
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Process: {selectedProductionProcess?.name || 'None'}</span>
                </div>
              </div>
              <button onClick={() => handleCloseModal(setShowAddLineVerificationModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <div className="px-8 py-8">
              <form id="lineVerificationForm" onSubmit={handleSaveLineVerification}>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Field Name</label>
                <input name="verificationFieldName" defaultValue={editingItem?.name} required className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:outline-none" placeholder="e.g., Number of Plies" type="text" />
                <p className="mt-2 text-[11px] text-slate-500 font-medium">Enter a unique name for the verification field to be used in inspections.</p>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
              <button onClick={() => handleCloseModal(setShowAddLineVerificationModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter">Cancel</button>
              <button form="lineVerificationForm" type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Field' : 'Save Field'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Line Product Category Modal */}
      {showAddLineProductModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Product Category' : 'Add Product Category'}</h3>
              <button onClick={() => handleCloseModal(setShowAddLineProductModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <div className="px-8 py-8">
              <form id="lineProductCategoryForm" onSubmit={handleSaveLineProductCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Category Name</label>
                  <input name="categoryName" defaultValue={editingItem?.name} required className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g., Curtain, Rugs" type="text" />
                  <p className="mt-2 text-[11px] text-slate-500 font-medium">Enter a unique name for the product category to be used in inspections.</p>
                </div>
                {selectedProductionProcess && (
                  <p className="text-xs text-blue-600 font-medium">Will be linked to: <strong>{selectedProductionProcess.name}</strong></p>
                )}
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
              <button disabled={isLoading} onClick={() => handleCloseModal(setShowAddLineProductModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter disabled:opacity-50">Cancel</button>
              <button form="lineProductCategoryForm" type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Category' : 'Save Category'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Line Production Process Modal */}
      {showAddProductionProcessModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Production Process' : 'Add Production Process'}</h3>
              <button onClick={() => handleCloseModal(setShowAddProductionProcessModal)} className="text-slate-400 hover:text-slate-600">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="px-6 py-8">
              <form id="productionProcessForm" onSubmit={handleSaveProductionProcess} className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Process Name</label>
                <input name="processName" defaultValue={editingItem?.name} required className="w-full rounded-lg border-slate-200 py-3 text-sm font-bold focus:border-[#136bce] focus:ring-[#136bce] bg-slate-50 focus:bg-white" placeholder="e.g., Cutting, Stitching" type="text" />
                <p className="text-[11px] text-slate-400 font-medium">Enter the name of the sequential manufacturing step.</p>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4 rounded-b-2xl">
              <button disabled={isLoading} onClick={() => handleCloseModal(setShowAddProductionProcessModal)} className="rounded-lg px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter disabled:opacity-50">Cancel</button>
              <button form="productionProcessForm" type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Process' : 'Save Process'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Container Checklist Item Modal */}
      {showAddContainerChecklistItemModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Checklist Item' : 'Add Checklist Item'}</h3>
              <button onClick={() => handleCloseModal(setShowAddContainerChecklistItemModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <div className="px-8 py-8">
              <form id="containerChecklistForm" onSubmit={handleSaveContainerChecklistItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Item Name</label>
                  <input name="itemName" defaultValue={editingItem?.name} required className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g., Roof Sealing" type="text" />
                  <p className="mt-2 text-[11px] text-slate-500 font-medium">Enter a unique name for the container checklist item.</p>
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
              <button disabled={isLoading} onClick={() => handleCloseModal(setShowAddContainerChecklistItemModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter disabled:opacity-50">Cancel</button>
              <button
                form="containerChecklistForm"
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70 active:scale-[0.98]"
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Item' : 'Save Item'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Container Image Label Modal */}
      {showAddContainerImageLabelModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Image Label' : `Add ${showAddContainerImageLabelModal === 'mandatory' ? 'Mandatory' : 'Optional'} Image`}</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Container Inspection / {showAddContainerImageLabelModal === 'mandatory' ? 'Mandatory' : 'Optional'} Images</p>
              </div>
              <button onClick={() => { setShowAddContainerImageLabelModal(null); setEditingItem(null); }} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <div className="px-8 py-8">
              <form id="stuffingImageForm" onSubmit={handleSaveStuffingImage} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Image Label</label>
                  <input name="itemLabel" defaultValue={editingItem?.label} required className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g., Empty Container" type="text" />
                  <p className="mt-2 text-[11px] text-slate-500 font-medium">Enter a descriptive label for the required documentation image.</p>
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
              <button disabled={isLoading} onClick={() => { setShowAddContainerImageLabelModal(null); setEditingItem(null); }} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter disabled:opacity-50">Cancel</button>
              <button
                form="stuffingImageForm"
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70 active:scale-[0.98]"
              >
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update Label' : 'Save Label'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Defect Category Modal */}
      {showAddDefectCategoryModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined font-semibold">category</span></div>
                <div><h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Defect Category' : 'Add Defect Category'}</h3><p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">Master Data / {activeTab}</p></div>
              </div>
              <button onClick={() => handleCloseModal(setShowAddDefectCategoryModal)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <form onSubmit={activeTab === 'Line Inspection' ? handleSaveLineDefectCategory : handleSaveFabricDefectCategory}>
            <div className="px-6 py-8">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Defect Category Name</label>
                <input name="defectCategoryName" defaultValue={editingItem?.name} className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g. Surface Imperfections" type="text" required />
                <p className="text-[10px] text-slate-400 mt-1 font-bold italic tracking-tight">Provide a unique name to group specific defect types.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 border-t border-slate-100">
              <button type="button" onClick={() => handleCloseModal(setShowAddDefectCategoryModal)} className="rounded-lg px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-200 uppercase tracking-widest">Cancel</button>
              <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-blue-500/25 hover:bg-blue-700 uppercase tracking-widest disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                {editingItem ? 'Update Category' : 'Save Category'}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Defect Type Modal */}
      {showAddDefectTypeModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md scale-100 rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Defect Type' : 'Add Defect Type'}</h3>
              <button onClick={() => handleCloseModal(setShowAddDefectTypeModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined">close</span></button>
            </div>
            <form onSubmit={activeTab === 'Line Inspection' ? handleSaveLineDefectType : handleSaveFabricDefectType}>
            <div className="p-6">
              <div className="space-y-5">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">Defect Category</label>
                  <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-slate-600">
                    <span className="material-symbols-outlined text-slate-400 text-[20px]">folder_open</span>
                    <span className="text-sm font-bold">{(activeTab === 'Fabric Inspection' ? selectedDefectCategory : selectedLineDefectCategory)}</span>
                  </div>
                </div>
                <div><label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">DCL Code</label><input name="defectCode" defaultValue={editingItem?.code} className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-mono uppercase font-bold focus:border-[#136bce]" placeholder="ENTER CODE (E.G. YD-001)" type="text" required /></div>
                <div><label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">Defect Name</label><input name="defectName" defaultValue={editingItem?.name} className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-bold focus:border-[#136bce]" placeholder="Enter defect name (e.g. Slubs)" type="text" required /></div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4 rounded-b-2xl">
              <button type="button" onClick={() => handleCloseModal(setShowAddDefectTypeModal)} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 uppercase tracking-tighter">Cancel</button>
              <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                {editingItem ? 'Update Defect' : 'Save Defect'}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Checklist Modal */}
      {showAddChecklistModal || showAddLineChecklistModal ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined">playlist_add_check</span></div>
                <div><h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Checklist' : 'Add New Checklist'}</h3><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{editingItem ? 'Modify existing quality checkpoint' : 'Define a new quality checkpoint for inspection'}</p></div>
              </div>
              <button onClick={() => { handleCloseModal(setShowAddChecklistModal); handleCloseModal(setShowAddLineChecklistModal); }} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <form onSubmit={showAddLineChecklistModal ? handleSaveLineChecklist : handleSaveFabricChecklist}>
            <div className="px-6 py-6">
              <div className="space-y-5">
                <div><label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Checklist Title</label><input name="checklistTitle" defaultValue={editingItem?.title} className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-bold focus:bg-white focus:border-[#136bce]" placeholder="e.g., Fabric Width Consistency" type="text" required /></div>
                <div><label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Description</label><textarea name="checklistDescription" defaultValue={editingItem?.description} className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium focus:bg-white focus:border-[#136bce] resize-none" placeholder="Enter detailed inspection instructions..." rows={4}></textarea></div>
                <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm"><span className="material-symbols-outlined text-[18px] text-slate-600">photo_camera</span></div>
                    <div><p className="text-sm font-bold text-slate-800">Evidence Required</p><p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Inspector must upload a photo to verify this step</p></div>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center"><input name="evidenceRequired" defaultChecked={editingItem?.evidence_required} className="peer sr-only" type="checkbox" /><div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#136bce] peer-checked:after:translate-x-full peer-checked:after:border-white"></div></label>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button type="button" onClick={() => { handleCloseModal(setShowAddChecklistModal); handleCloseModal(setShowAddLineChecklistModal); }} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 uppercase tracking-widest">Cancel</button>
              <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-blue-700 uppercase tracking-widest disabled:opacity-70">
                {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                <span>{editingItem ? 'Update' : 'Save'}</span>
              </button>
            </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Add/Edit Fabric Inspection Image Modal */}
      {showAddFabricImageModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined">photo_camera</span></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Image' : `Add ${showAddFabricImageModal === 'mandatory' ? 'Mandatory' : 'Optional'} Image`}</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Fabric Inspection / {showAddFabricImageModal === 'mandatory' ? 'Mandatory' : 'Optional'} Images</p>
                </div>
              </div>
              <button onClick={() => { setShowAddFabricImageModal(null); setEditingItem(null); }} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <form onSubmit={handleSaveFabricImage}>
              <div className="px-6 py-6">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Image Name</label>
                  <input name="imageLabel" defaultValue={editingItem?.label} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-bold focus:bg-white focus:border-[#136bce]" placeholder="e.g., Front view of fabric roll" type="text" required />
                  <p className="mt-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-tight">Provide a descriptive name for this inspection image</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                <button type="button" onClick={() => { setShowAddFabricImageModal(null); setEditingItem(null); }} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 uppercase tracking-widest">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-blue-700 uppercase tracking-widest disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  <span>{editingItem ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== FINAL INSPECTION MODALS ===== */}

      {/* Add/Edit Final Product Category Modal */}
      {showAddFinalProductModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Product Category' : 'Add Product Category'}</h3>
              <button onClick={() => handleCloseModal(setShowAddFinalProductModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <form onSubmit={handleSaveFinalProductCategory}>
              <div className="px-8 py-8">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Category Name</label>
                    <input 
                      name="categoryName"
                      defaultValue={editingItem?.name} 
                      required
                      className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" 
                      placeholder="e.g., T-Shirt, Jeans" 
                      type="text"
                    />
                    <p className="mt-2 text-[11px] text-slate-500 font-medium">Enter a unique name for the product category to be used in final inspections.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
                <button type="button" onClick={() => handleCloseModal(setShowAddFinalProductModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  <span>{editingItem ? 'Update Category' : 'Save Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Final Defect Category Modal */}
      {showAddFinalDefectCategoryModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined font-semibold">category</span></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Defect Category' : 'Add Defect Category'}</h3>
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">Master Data / Final Inspection</p>
                </div>
              </div>
              <button type="button" onClick={() => handleCloseModal(setShowAddFinalDefectCategoryModal)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <form onSubmit={handleSaveFinalDefectCategory}>
              <div className="px-6 py-8">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Defect Category Name</label>
                  <input name="categoryName" defaultValue={editingItem?.name} required className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g. Sewing Defects" type="text"/>
                  <p className="text-[10px] text-slate-400 mt-1 font-bold italic tracking-tight">Provide a unique name to group specific defect types for final inspection.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => handleCloseModal(setShowAddFinalDefectCategoryModal)} className="rounded-lg px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-200 uppercase tracking-widest">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-xs font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-widest disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  {editingItem ? 'Update Category' : 'Save Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Final Defect Type Modal */}
      {showAddFinalDefectTypeModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-md scale-100 rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Defect Type' : 'Add Defect Type'}</h3>
              <button type="button" onClick={() => handleCloseModal(setShowAddFinalDefectTypeModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined">close</span></button>
            </div>
            <form onSubmit={handleSaveFinalDefectType}>
              <div className="p-6">
                <div className="space-y-5">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">Defect Category</label>
                    <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-slate-600">
                      <span className="material-symbols-outlined text-slate-400 text-[20px]">folder_open</span>
                      <span className="text-sm font-bold">{selectedFinalCategoryName}</span>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">DCL Code</label>
                    <input name="defectCode" defaultValue={editingItem?.code} required className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-mono uppercase font-bold focus:border-[#136bce]" placeholder="ENTER CODE (E.G. FI-001)" type="text"/>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-400">Defect Name</label>
                    <input name="defectName" defaultValue={editingItem?.name} required className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-bold focus:border-[#136bce]" placeholder="Enter defect name (e.g. Broken Stitch)" type="text"/>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4 rounded-b-2xl">
                <button type="button" onClick={() => handleCloseModal(setShowAddFinalDefectTypeModal)} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 uppercase tracking-tighter">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  {editingItem ? 'Update Defect' : 'Save Defect'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Final PO Checklist Modal */}
      {showAddFinalChecklistModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-[#136bce]"><span className="material-symbols-outlined">playlist_add_check</span></div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit Checklist' : 'Add New Checklist'}</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Final Inspection quality checkpoint</p>
                </div>
              </div>
              <button onClick={() => handleCloseModal(setShowAddFinalChecklistModal)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><span className="material-symbols-outlined text-[20px]">close</span></button>
            </div>
            <form onSubmit={handleSaveFinalPoChecklist}>
              <div className="px-6 py-6">
                <div className="space-y-5">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Checklist Title</label>
                    <input 
                      name="checklistTitle"
                      defaultValue={editingItem?.title} 
                      className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-bold focus:bg-white focus:border-[#136bce]" 
                      placeholder="e.g., Packaging Integrity" 
                      type="text"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-400">Description</label>
                    <textarea 
                      name="checklistDesc"
                      defaultValue={editingItem?.description} 
                      className="w-full rounded-lg border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium focus:bg-white focus:border-[#136bce] resize-none" 
                      placeholder="Enter detailed inspection instructions..." 
                      rows={4}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4 border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm"><span className="material-symbols-outlined text-[18px] text-slate-600">photo_camera</span></div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">Evidence Required</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Inspector must upload a photo to verify this step</p>
                      </div>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input 
                        name="evidenceRequired"
                        defaultChecked={editingItem?.evidence_required} 
                        className="peer sr-only" 
                        type="checkbox"
                      />
                      <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#136bce] peer-checked:after:translate-x-full peer-checked:after:border-white"></div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                <button type="button" onClick={() => handleCloseModal(setShowAddFinalChecklistModal)} className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 uppercase tracking-widest">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-blue-700 uppercase tracking-widest disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  <span>{editingItem ? 'Update' : 'Save'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Final Image Modal (Mandatory / Optional) */}
      {showAddFinalImageModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit Image Label' : `Add ${showAddFinalImageModal === 'mandatory' ? 'Mandatory' : 'Optional'} Image`}</h3>
              <button onClick={() => handleCloseModal(setShowAddFinalImageModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <form onSubmit={handleSaveFinalInspectionImage}>
              <div className="px-8 py-8">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Label Name</label>
                    <input 
                      name="imageLabel"
                      defaultValue={editingItem?.label} 
                      required 
                      className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" 
                      placeholder="e.g., Full Garment Front" 
                      type="text"
                    />
                    <p className="mt-2 text-[11px] text-slate-500 font-medium">Define a new label for {showAddFinalImageModal} final inspection photos.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
                <button type="button" onClick={() => handleCloseModal(setShowAddFinalImageModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter">Cancel</button>
                <button type="submit" disabled={isLoading} className="flex items-center gap-2 rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-70">
                  {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>}
                  {editingItem ? 'Update Label' : 'Save Label'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload SOP Document Modal */}
      {showAddSOPModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">{editingItem ? 'Edit SOP Document' : 'Upload SOP Document'}</h3>
              <button onClick={() => handleCloseModal(setShowAddSOPModal)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <form onSubmit={handleSaveSopDocument}>
              <div className="px-8 py-8">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Buyer Name</label>
                    <select name="buyer_id" defaultValue={editingItem?.buyer_id || selectedBuyerFolder?.id || ""} className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" required>
                      <option value="">Select Buyer</option>
                      {buyers.map((buyer: any) => (
                        <option key={buyer.id} value={buyer.id}>{buyer.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Document Title</label>
                    <input name="title" defaultValue={editingItem?.title} className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10" placeholder="e.g., Quality Standards 2024" type="text" required/>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Attach Document</label>
                    <div className="relative">
                      <input className="hidden" id="sop-file" name="file" type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png"/>
                      <label htmlFor="sop-file" className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 transition-all hover:border-[#136bce] hover:bg-blue-50/30">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
                          <span className="material-symbols-outlined text-[#136bce]">upload_file</span>
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-bold text-slate-700">{editingItem ? editingItem.file_name : 'Click to upload or drag and drop'}</p>
                          <p className="text-[10px] font-medium text-slate-400">PDF, DOCX up to 10MB</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
                <button type="button" onClick={() => handleCloseModal(setShowAddSOPModal)} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter">Cancel</button>
                <button type="submit" disabled={isLoading} className="rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter disabled:opacity-50">{editingItem ? 'Update Document' : 'Save Document'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Buyer Folder Modal */}
      {showAddBuyerModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 shadow-2xl transition-all animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5">
              <h3 className="text-xl font-bold text-slate-900">Create Buyer Folder</h3>
              <button onClick={() => { handleCloseModal(setShowAddBuyerModal); setNewBuyerName(''); }} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                <span className="material-symbols-outlined block">close</span>
              </button>
            </div>
            <div className="px-8 py-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Buyer Name</label>
                  <input
                    value={newBuyerName}
                    onChange={(e) => setNewBuyerName(e.target.value)}
                    className="w-full rounded-xl border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold transition-all focus:border-[#136bce] focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                    placeholder="e.g., Nike, Adidas, Levi's"
                    type="text"
                  />
                  <p className="mt-2 text-[11px] text-slate-500 font-medium">Create a dedicated folder to organize SOP documents for this buyer.</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 bg-slate-50 px-8 py-6 border-t border-slate-100">
              <button onClick={() => { handleCloseModal(setShowAddBuyerModal); setNewBuyerName(''); }} className="rounded-xl px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200 uppercase tracking-tighter">Cancel</button>
              <button onClick={handleCreateBuyerFolder} className="rounded-xl bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/25 transition-all hover:bg-blue-700 uppercase tracking-tighter">Create Folder</button>
            </div>
          </div>
        </div>
      )}

      {/* SOP Document Preview Modal */}
      {sopPreviewDoc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => { URL.revokeObjectURL(sopPreviewDoc.url); setSopPreviewDoc(null); }}
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#136bce]">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">{sopPreviewDoc.name}</h4>
                  <p className="text-[10px] text-slate-500 font-medium">Attachment Preview</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={sopPreviewDoc.url}
                  download={sopPreviewDoc.name}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-blue-50 hover:text-[#136bce] transition-all"
                  title="Download File"
                >
                  <span className="material-symbols-outlined text-[20px]">download</span>
                </a>
                <button
                  onClick={() => { URL.revokeObjectURL(sopPreviewDoc.url); setSopPreviewDoc(null); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all"
                  title="Close"
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
              </div>
            </div>
            <div className="p-4 bg-slate-50 flex-1 overflow-auto flex flex-col items-center justify-center min-h-[300px] text-center rounded-b-2xl">
              {/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(sopPreviewDoc.name) ? (
                <img src={sopPreviewDoc.url} alt={sopPreviewDoc.name} className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm border border-slate-200 bg-white" />
              ) : /\.(pdf)$/i.test(sopPreviewDoc.name) ? (
                <iframe src={sopPreviewDoc.url} title={sopPreviewDoc.name} className="w-full h-[70vh] rounded-lg border border-slate-200 shadow-sm bg-white"></iframe>
              ) : /\.(xlsx|xls)$/i.test(sopPreviewDoc.name) ? (
                <ExcelPreview blob={sopPreviewDoc.blob!} fileName={sopPreviewDoc.name} downloadUrl={sopPreviewDoc.url} />
              ) : /\.docx$/i.test(sopPreviewDoc.name) ? (
                <WordPreview blob={sopPreviewDoc.blob!} fileName={sopPreviewDoc.name} downloadUrl={sopPreviewDoc.url} />
              ) : /\.doc$/i.test(sopPreviewDoc.name) ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center mb-4 shadow-lg">
                    <span className="material-symbols-outlined text-[40px] text-white">article</span>
                  </div>
                  <h5 className="text-base font-bold text-slate-800 mb-1">Legacy Word Document (.doc)</h5>
                  <p className="text-xs text-slate-500 max-w-xs leading-relaxed mb-6">
                    Old-format <span className="font-semibold text-slate-700">.doc</span> files cannot be previewed in the browser. Download to open in Microsoft Word or Google Docs.
                  </p>
                  <a
                    href={sopPreviewDoc.url}
                    download={sopPreviewDoc.name}
                    className="px-6 py-2.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md active:scale-95 flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                    Download to Open
                  </a>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center mb-4 border border-slate-100">
                    <span className="material-symbols-outlined text-[40px] text-slate-300">draft</span>
                  </div>
                  <h5 className="text-base font-bold text-slate-800 mb-2">Preview Not Available</h5>
                  <p className="text-xs text-slate-500 max-w-xs leading-relaxed">
                    The file <span className="font-bold text-slate-700">{sopPreviewDoc.name}</span> cannot be previewed directly in the browser window.
                  </p>
                  <div className="mt-8 flex gap-3">
                    <a
                      href={sopPreviewDoc.url}
                      download={sopPreviewDoc.name}
                      className="px-6 py-2 bg-[#136bce] text-white text-xs font-bold rounded-xl hover:bg-[#0e52a0] transition-all shadow-md active:scale-95 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[18px]">download</span>
                      Download File
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Result Modal — shown after Facility/Supplier xlsx import */}
      {importResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="relative inline-block transform overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle animate-in zoom-in-95 duration-200">
            {/* Gradient header */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#136bce] via-[#1e7fd6] to-[#3b9ce0] px-8 py-6">
              <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-white/10"></div>
              <div className="absolute -bottom-6 -left-6 h-20 w-20 rounded-full bg-white/10"></div>
              <div className="relative flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/30 backdrop-blur-sm">
                  <span className="material-symbols-outlined text-white text-[26px]">task_alt</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Import Complete</h3>
                  <p className="text-xs text-white/80">
                    {importResult.entity === 'Facility' ? 'Facilities'
                      : importResult.entity === 'Supplier' ? 'Suppliers'
                      : importResult.entity === 'FabricDefects' ? 'Fabric Defect Categories & Types'
                      : importResult.entity === 'FinalDefects' ? 'Final Defect Categories & Types'
                      : importResult.entity === 'ProductionProcesses' ? 'Production Processes'
                      : importResult.entity === 'LineDefects' ? 'Line Defect Categories & Types'
                      : importResult.entity === 'LineProductCategories' ? 'Line Product Categories'
                      : importResult.entity === 'LineChecklists' ? 'Line Checklists'
                      : importResult.entity === 'LineVerifications' ? 'Line Verification Fields'
                      : importResult.entity === 'LineMandatoryImages' ? 'Line Mandatory Images'
                      : importResult.entity === 'LineOptionalImages' ? 'Line Optional Images'
                      : importResult.entity === 'FabricChecklists' ? 'Fabric Checklists'
                      : importResult.entity === 'FabricMandatoryImages' ? 'Fabric Mandatory Inspection Images'
                      : importResult.entity === 'FabricOptionalImages' ? 'Fabric Optional Inspection Images'
                      : importResult.entity === 'FinalProductCategories' ? 'Final Product Categories'
                      : importResult.entity === 'FinalMandatoryImages' ? 'Final Mandatory Product Images'
                      : importResult.entity === 'FinalOptionalImages' ? 'Final Optional Product Images'
                      : importResult.entity === 'FinalPoChecklists' ? 'Final PO Checklist'
                      : importResult.entity === 'PointsChecklists' ? 'Container Points Checklist'
                      : importResult.entity === 'FinalVerificationChecklists' ? 'Container Final Verification Checklist'
                      : importResult.entity === 'StuffingMandatoryImages' ? 'Container Mandatory Stuffing Images'
                      : importResult.entity === 'StuffingOptionalImages' ? 'Container Optional Stuffing Images'
                      : 'Container Stuffing Images'} · {importResult.total} row{importResult.total !== 1 ? 's' : ''} processed
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Split stats — only for FabricDefects (Flavor 1) */}
              {importResult.categoryStats && (
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Defect Categories</p>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <span className="material-symbols-outlined text-emerald-600 text-[16px]">add_circle</span>
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Inserted</span>
                      </div>
                      <p className="text-xl font-bold text-emerald-700">{importResult.categoryStats.inserted}</p>
                    </div>
                    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <span className="material-symbols-outlined text-blue-600 text-[16px]">sync</span>
                        <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Replaced</span>
                      </div>
                      <p className="text-xl font-bold text-blue-700">{importResult.categoryStats.replaced}</p>
                    </div>
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Defect Types</p>
                </div>
              )}

              {/* Stat tiles (Inserted / Replaced / Failed) — types-level for FabricDefects, all-level for others */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <span className="material-symbols-outlined text-emerald-600 text-[18px]">add_circle</span>
                    <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Inserted</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">{importResult.inserted}</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <span className="material-symbols-outlined text-blue-600 text-[18px]">sync</span>
                    <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Replaced</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">{importResult.replaced}</p>
                </div>
                <div className={`rounded-xl border p-4 text-center ${importResult.failed > 0 ? 'border-red-200 bg-red-50/60' : 'border-slate-200 bg-slate-50/60'}`}>
                  <div className="flex items-center justify-center gap-1.5 mb-1.5">
                    <span className={`material-symbols-outlined text-[18px] ${importResult.failed > 0 ? 'text-red-600' : 'text-slate-500'}`}>error</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${importResult.failed > 0 ? 'text-red-700' : 'text-slate-600'}`}>Failed</span>
                  </div>
                  <p className={`text-2xl font-bold ${importResult.failed > 0 ? 'text-red-700' : 'text-slate-700'}`}>{importResult.failed}</p>
                </div>
              </div>

              {/* Helper text */}
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-6">
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-700">Replaced</span> = rows whose every field already matched an existing record (no duplicate created). <span className="font-semibold text-slate-700">Failed</span> rows are skipped — see details below.
                </p>
              </div>

              {/* Error list */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Errors ({importResult.errors.length})</p>
                  <div className="rounded-lg border border-red-200 bg-red-50/40 max-h-40 overflow-y-auto divide-y divide-red-100">
                    {importResult.errors.map((e, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start gap-2">
                        <span className="font-mono font-bold text-red-700 shrink-0">Row {e.row}</span>
                        <span className="text-red-600">{e.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setImportResult(null)}
                className="w-full rounded-lg bg-[#136bce] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line Import — Cross-process mismatch confirmation (shared across all 4 Line sections) */}
      {pendingProcessImport && (() => {
        const entity = pendingProcessImport.entity;
        const sectionLabel =
          entity === 'LineDefects' ? 'Defect Categories & Types'
          : entity === 'LineProductCategories' ? 'Product Categories'
          : entity === 'LineChecklists' ? 'Checklists'
          : entity === 'LineVerifications' ? 'Verification Fields'
          : entity === 'LineMandatoryImages' ? 'Mandatory Images'
          : 'Optional Images';
        const importInputRef =
          entity === 'LineDefects' ? lineDefectImportInputRef
          : entity === 'LineProductCategories' ? lineProductCatImportInputRef
          : entity === 'LineChecklists' ? lineChecklistImportInputRef
          : entity === 'LineVerifications' ? lineVerificationImportInputRef
          : entity === 'LineMandatoryImages' ? lineMandatoryImageImportInputRef
          : lineOptionalImageImportInputRef;
        const isBusy = isImporting === entity;
        const proceed = async () => {
          const rows = pendingProcessImport.rows;
          setPendingProcessImport(null);
          if (entity === 'LineDefects') await submitLineImport(rows);
          else if (entity === 'LineProductCategories') await submitLineProductCategoriesImport(rows);
          else if (entity === 'LineChecklists') await submitLineChecklistsImport(rows);
          else if (entity === 'LineVerifications') await submitLineVerificationsImport(rows);
          else if (entity === 'LineMandatoryImages') await submitLineImagesImport(rows, 'mandatory');
          else await submitLineImagesImport(rows, 'optional');
        };
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
            <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle animate-in zoom-in-95 duration-200">
              <div className="flex items-center gap-3 bg-amber-500 px-6 py-3">
                <span className="material-symbols-outlined text-white text-[20px]">warning</span>
                <p className="text-sm font-semibold text-white">Different Production Process</p>
              </div>
              <div className="p-8">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-4 ring-amber-50/50">
                    <span className="material-symbols-outlined text-[32px]">swap_horiz</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Cross-Process Import</h3>
                  <p className="text-sm text-slate-500 mb-1">
                    Importing <span className="font-semibold text-slate-700">{sectionLabel}</span>.
                  </p>
                  <p className="text-sm text-slate-500 mb-3">
                    This file was exported from a different production process. Continue?
                  </p>
                  <div className="w-full space-y-2 text-sm">
                    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">File from</span>
                      <span className="font-semibold text-slate-700 truncate ml-2">{pendingProcessImport.fileProcessName}</span>
                    </div>
                    <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#136bce]">Importing into</span>
                      <span className="font-semibold text-[#136bce] truncate ml-2">{pendingProcessImport.currentProcessName}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingProcessImport(null);
                      if (importInputRef.current) importInputRef.current.value = '';
                    }}
                    disabled={isBusy}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={proceed}
                    disabled={isBusy}
                    className="flex-1 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    Continue Import
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default MasterData;
